import React, { useState, useEffect, useMemo, useRef } from 'react';
import { BrowserRouter, Routes, Route, NavLink, useParams } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || '/api';

// ---------------------------------------------------------------------------
// Auth — token stored in memory only (never localStorage/sessionStorage)
// ---------------------------------------------------------------------------
let _token = null;
let _onUnauth = null;

function apiFetch(url, options = {}) {
  const headers = { ...options.headers };
  if (_token) headers['Authorization'] = `Bearer ${_token}`;
  return fetch(url, { ...options, headers }).then(r => {
    if (r.status === 401) {
      _token = null;
      _onUnauth?.();
      throw new Error('Session expired — please log in again');
    }
    return r;
  });
}

// ---------------------------------------------------------------------------
// useFetch
// ---------------------------------------------------------------------------
function useFetch(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiFetch(url)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [url]);

  return { data, loading, error };
}

// ---------------------------------------------------------------------------
// Utility components
// ---------------------------------------------------------------------------
function Spinner() {
  return (
    <div className="flex justify-center items-center py-12">
      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ErrorMsg({ msg }) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
      Error: {msg}
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="text-center py-12 text-gray-400">
      <div className="text-4xl mb-2">📭</div>
      <p>No {label} found</p>
    </div>
  );
}

function RoleBadge({ role }) {
  const colors = {
    ADMIN: 'bg-red-100 text-red-800',
    SUPERVISOR: 'bg-orange-100 text-orange-800',
    BOM_CONTROLLER: 'bg-orange-100 text-orange-800',
    VIEWER: 'bg-gray-100 text-gray-700',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${colors[role] || 'bg-gray-100 text-gray-700'}`}>
      {role}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Dashboard page
// ---------------------------------------------------------------------------
function SummaryCard({ title, value, icon, color }) {
  return (
    <div className={`bg-white rounded-xl shadow p-6 border-l-4 ${color}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-3xl font-bold text-gray-800 mt-1">
            {value === null ? (
              <span className="inline-block w-8 h-8 border-4 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
            ) : value}
          </p>
        </div>
        <span className="text-4xl">{icon}</span>
      </div>
    </div>
  );
}

const DISTRICT_SUMMARY_COLUMNS = ['NEW', 'CIVIL_PENDING', 'CIVIL_DONE', 'INSTLN_DONE', 'UNINSTALLED'];

function Dashboard() {
  const sites = useFetch(`${API}/sites`);
  const faults = useFetch(`${API}/fault-tasks/open`);
  const personnel = useFetch(`${API}/personnel`);
  const districts = useFetch(`${API}/districts`);
  const districtSummary = useFetch(`${API}/dashboard/install-status-by-district`);

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Dashboard</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
        <SummaryCard
          title="Total Sites"
          value={sites.loading ? null : (sites.data?.length ?? 0)}
          icon="🗺️"
          color="border-blue-500"
        />
        <SummaryCard
          title="Open Faults"
          value={faults.loading ? null : (faults.data?.length ?? 0)}
          icon="⚠️"
          color="border-red-500"
        />
        <SummaryCard
          title="Active Personnel"
          value={personnel.loading ? null : (personnel.data?.filter(p => p.is_active).length ?? 0)}
          icon="👷"
          color="border-green-500"
        />
        <SummaryCard
          title="Districts"
          value={districts.loading ? null : (districts.data?.length ?? 0)}
          icon="📍"
          color="border-purple-500"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">Recent Open Faults</h2>
          {faults.loading && <Spinner />}
          {faults.error && <ErrorMsg msg={faults.error} />}
          {!faults.loading && !faults.error && (
            faults.data?.length === 0
              ? <EmptyState label="open faults" />
              : <div className="space-y-2">
                  {faults.data.slice(0, 5).map(f => (
                    <div key={f.id} className="flex justify-between items-center py-2 border-b last:border-0">
                      <span className="text-sm font-medium text-gray-700">{f.fault_type}</span>
                      <span className="text-xs text-gray-500">{f.reported_date}</span>
                    </div>
                  ))}
                </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">Install Status Summary</h2>
          {sites.loading && <Spinner />}
          {sites.error && <ErrorMsg msg={sites.error} />}
          {!sites.loading && !sites.error && (
            sites.data?.length === 0
              ? <EmptyState label="sites" />
              : (() => {
                  const counts = sites.data.reduce((acc, s) => {
                    acc[s.site_status] = (acc[s.site_status] || 0) + 1;
                    return acc;
                  }, {});
                  return (
                    <div className="space-y-2">
                      {Object.entries(counts).map(([status, count]) => (
                        <div key={status} className="flex justify-between items-center py-2 border-b last:border-0">
                          <SiteStatusBadge status={status} />
                          <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-0.5 rounded-full">{count}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-6 mt-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">District-wise Install Status</h2>
        {districtSummary.loading && <Spinner />}
        {districtSummary.error && <ErrorMsg msg={districtSummary.error} />}
        {!districtSummary.loading && !districtSummary.error && (
          districtSummary.data?.length === 0
            ? <EmptyState label="districts" />
            : (() => {
                const rows = [...districtSummary.data].sort(
                  (a, b) => (b.counts.INSTLN_DONE || 0) - (a.counts.INSTLN_DONE || 0)
                );
                return (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="border-b">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                            District
                          </th>
                          {DISTRICT_SUMMARY_COLUMNS.map(c => (
                            <th key={c} className="px-3 py-2 text-center whitespace-nowrap">
                              <SiteStatusBadge status={c} />
                            </th>
                          ))}
                          <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {rows.map(r => (
                          <tr key={r.district_id || 'unassigned'} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-medium text-gray-700 whitespace-nowrap">{r.district_name}</td>
                            {DISTRICT_SUMMARY_COLUMNS.map(c => (
                              <td key={c} className="px-3 py-2 text-center">
                                <span className={`inline-flex items-center justify-center min-w-[1.75rem] px-1.5 py-0.5 rounded text-xs font-bold ${SITE_STATUS_COLORS[c]}`}>
                                  {r.counts[c] || 0}
                                </span>
                              </td>
                            ))}
                            <td className="px-3 py-2 text-center font-semibold text-gray-800">{r.total}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sites page
// ---------------------------------------------------------------------------
const INSTALL_STATUSES = [
  'GOVT_PROVIDED', 'SURVEY_PENDING', 'SURVEYED', 'APPROVED',
  'SENDING_CIVIL_MTLS', 'CIVIL_MTLS_DISPATCHED', 'CIVIL_MTLS_RECEIVED',
  'CIVIL_PENDING', 'CIVIL_DONE',
  'SENDING_UNITS', 'UNITS_DISPATCHED', 'UNITS_RECEIVED',
  'INSTLN_PENDING', 'INSTLN_COMPLETE', 'READY_FOR_INSTALL',
  'INSTALLED', 'COMMISSIONED',
  'ON_HOLD', 'NO_ACTION', 'REJECTED',
];

function InstallBadge({ status }) {
  const colors = {
    GOVT_PROVIDED:          'bg-gray-100 text-gray-600',
    SURVEY_PENDING:         'bg-gray-100 text-gray-500',
    SURVEYED:               'bg-blue-100 text-blue-800',
    APPROVED:               'bg-indigo-100 text-indigo-800',
    SENDING_CIVIL_MTLS:     'bg-amber-100 text-amber-800',
    CIVIL_MTLS_DISPATCHED:  'bg-amber-200 text-amber-900',
    CIVIL_MTLS_RECEIVED:    'bg-yellow-100 text-yellow-800',
    CIVIL_PENDING:          'bg-orange-50 text-orange-700',
    CIVIL_DONE:             'bg-orange-100 text-orange-800',
    SENDING_UNITS:          'bg-sky-100 text-sky-800',
    UNITS_DISPATCHED:       'bg-sky-200 text-sky-900',
    UNITS_RECEIVED:         'bg-cyan-100 text-cyan-800',
    INSTLN_PENDING:         'bg-teal-100 text-teal-800',
    INSTLN_COMPLETE:        'bg-teal-200 text-teal-900',
    READY_FOR_INSTALL:      'bg-yellow-100 text-yellow-800',
    INSTALLED:              'bg-green-100 text-green-800',
    COMMISSIONED:           'bg-green-700 text-white',
    ON_HOLD:                'bg-gray-200 text-gray-600',
    NO_ACTION:              'bg-gray-300 text-gray-500',
    REJECTED:               'bg-red-100 text-red-700',
  };
  const cls = colors[status] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Three-track status model (site_status / mtl_status / jobs)
// ---------------------------------------------------------------------------
const SITE_STATUSES = [
  'NEW', 'CIVIL_PENDING', 'CIVIL_DONE', 'INSTLN_PENDING', 'INSTLN_DONE',
  'WORKING', 'CALIB_DUE', 'CALIB_DONE', 'FAULT', 'UNINSTALLED',
  'INACTIVE', 'ON_HOLD', 'NO_ACTION',
];

const MTL_STATUSES = ['NA', 'REQUESTED', 'DISPATCHED', 'RECEIVED', 'USED', 'RETURNED'];

const JOB_TYPES = ['CONSTRUCT', 'INSTALL', 'CALIB', 'ATTEND', 'RECEIVE', 'RETURN'];

function inferJobType(siteStatus) {
  if (siteStatus === 'CIVIL_PENDING') return 'CONSTRUCT';
  if (siteStatus === 'INSTLN_PENDING') return 'INSTALL';
  return '';
}

const SITE_STATUS_COLORS = {
  NEW:            'bg-gray-100 text-gray-600',
  CIVIL_PENDING:  'bg-orange-50 text-orange-700',
  CIVIL_DONE:     'bg-orange-100 text-orange-800',
  INSTLN_PENDING: 'bg-teal-100 text-teal-800',
  INSTLN_DONE:    'bg-teal-200 text-teal-900',
  WORKING:        'bg-green-700 text-white',
  CALIB_DUE:      'bg-yellow-100 text-yellow-800',
  CALIB_DONE:     'bg-green-100 text-green-800',
  FAULT:          'bg-red-100 text-red-700',
  UNINSTALLED:    'bg-gray-200 text-gray-600',
  INACTIVE:       'bg-gray-300 text-gray-500',
  ON_HOLD:        'bg-gray-200 text-gray-600',
  NO_ACTION:      'bg-gray-300 text-gray-500',
};

function SiteStatusBadge({ status }) {
  const cls = SITE_STATUS_COLORS[status] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${cls}`}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
}

function MtlStatusBadge({ status }) {
  const colors = {
    NA:         'bg-gray-100 text-gray-400',
    REQUESTED:  'bg-amber-100 text-amber-800',
    DISPATCHED: 'bg-amber-200 text-amber-900',
    RECEIVED:   'bg-cyan-100 text-cyan-800',
    USED:       'bg-blue-100 text-blue-800',
    RETURNED:   'bg-purple-100 text-purple-800',
  };
  const cls = colors[status] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${cls}`}>
      {status}
    </span>
  );
}

function ReceiptStatusBadge({ info }) {
  if (info.confirmed_at) {
    const date = new Date(info.confirmed_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap bg-cyan-100 text-cyan-800">
        Confirmed {date}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap bg-amber-100 text-amber-800">
      Pending
    </span>
  );
}

function SiteFormModal({ mode, site, projects, siteZones, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    govt_site_id: site?.govt_site_id || '',
    project_id: site?.project_id || (projects?.[0]?.id || ''),
    district_id: site?.district_id || '',
    zone: site?.zone || '',
    taluk: site?.taluk || '',
    gp: site?.gp || '',
    install_status: site?.install_status || 'GOVT_PROVIDED',
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [districtsLoading, setDistrictsLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!form.zone) {
      setDistrictOptions([]);
      return;
    }
    setDistrictsLoading(true);
    apiFetch(`${API}/districts?zone=${encodeURIComponent(form.zone)}`)
      .then(r => r.json())
      .then(d => { setDistrictOptions(d); setDistrictsLoading(false); })
      .catch(() => setDistrictsLoading(false));
  }, [form.zone]);

  const handleZoneChange = v => {
    setForm(f => ({ ...f, zone: v, district_id: '' }));
  };

  const handleDistrictChange = v => {
    setForm(f => ({ ...f, district_id: v }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      const isAdd = mode === 'add';
      const url = isAdd ? `${API}/sites` : `${API}/sites/${site.id}`;
      const method = isAdd ? 'POST' : 'PUT';
      const body = isAdd
        ? { ...form }
        : { govt_site_id: form.govt_site_id || null, zone: form.zone || null, district_id: form.district_id || null, taluk: form.taluk || null, gp: form.gp || null, install_status: form.install_status };
      const r = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      onSaved();
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-8">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-semibold text-gray-800">
            {mode === 'add' ? 'Add New Site' : (() => {
              const loc = [site.district_name, site.taluk, site.gp].filter(Boolean).join(' / ');
              return `Edit Site — ${site.govt_site_id || loc || site.id.slice(0, 8)}`;
            })()}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        {err && (
          <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'add' && (
            <div>
              <label className={labelCls}>Project</label>
              <select className={inputCls} value={form.project_id} onChange={e => set('project_id', e.target.value)} required>
                <option value="">Select project…</option>
                {projects?.map(p => (
                  <option key={p.id} value={p.id}>{p.project_name || p.contract_ref}</option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Zone</label>
              <select className={inputCls} value={form.zone} onChange={e => handleZoneChange(e.target.value)}>
                <option value="">Select zone…</option>
                {(siteZones || []).map(z => <option key={z}>{z}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>District</label>
              <select className={inputCls} value={form.district_id} onChange={e => handleDistrictChange(e.target.value)} required disabled={!form.zone || districtsLoading}>
                <option value="">{districtsLoading ? 'Loading…' : form.zone ? 'Select district…' : 'Select zone first'}</option>
                {districtOptions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Site ID <span className="text-gray-400 font-normal">(optional)</span></label>
            <input
              className={inputCls}
              value={form.govt_site_id}
              onChange={e => set('govt_site_id', e.target.value)}
              placeholder="e.g. KA-MYS-0042"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Taluk</label>
              <input
                className={inputCls}
                value={form.taluk}
                onChange={e => set('taluk', e.target.value)}
                placeholder="Taluk name"
              />
            </div>
            <div>
              <label className={labelCls}>Gram Panchayat</label>
              <input
                className={inputCls}
                value={form.gp}
                onChange={e => set('gp', e.target.value)}
                placeholder="GP name"
              />
            </div>
          </div>
          <div>
            <label className={labelCls}>Install Status</label>
            <select className={inputCls} value={form.install_status} onChange={e => set('install_status', e.target.value)}>
              {INSTALL_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60"
            >
              {saving ? 'Saving…' : (mode === 'add' ? 'Add Site' : 'Save Changes')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AssignModal({ site, personnel, onClose, onAssigned }) {
  const [staffId, setStaffId] = useState('');
  const [jobType, setJobType] = useState(() => inferJobType(site.site_status));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [activeJobs, setActiveJobs] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const activeStaff = (personnel || []).filter(p => p.is_active);
  const locLabel = [site.district_name, site.taluk, site.gp].filter(Boolean).join(' / ');

  const loadActiveJobs = () => {
    apiFetch(`${API}/sites/${site.id}/jobs`)
      .then(r => r.ok ? r.json() : [])
      .then(setActiveJobs)
      .catch(() => setActiveJobs([]));
  };

  useEffect(() => { loadActiveJobs(); }, [site.id]);

  const handleCancelJob = async (jobId) => {
    setCancellingId(jobId);
    try {
      const r = await apiFetch(`${API}/sites/${site.id}/jobs/${jobId}/cancel`, { method: 'POST' });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      loadActiveJobs();
    } catch (e) {
      setErr(e.message);
    } finally {
      setCancellingId(null);
    }
  };

  const handleAssign = async () => {
    if (!staffId || !jobType) return;
    setSaving(true);
    setErr(null);
    try {
      const r = await apiFetch(`${API}/sites/${site.id}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ staff_id: staffId, job_type: jobType }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      onAssigned();
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Assign Site</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="mb-4 bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
          <p className="text-xs font-medium text-gray-500 mb-1">📍 Site Location</p>
          <p className="text-sm font-semibold text-gray-800">{locLabel || '—'}</p>
          {site.govt_site_id && (
            <p className="text-xs text-gray-400 font-mono mt-0.5">{site.govt_site_id}</p>
          )}
        </div>
        {activeJobs && activeJobs.length > 0 && (
          <div className="mb-4 bg-amber-50 rounded-lg px-4 py-3 border border-amber-200">
            <p className="text-xs font-medium text-amber-700 mb-2">
              ⚠️ Existing active job{activeJobs.length > 1 ? 's' : ''} on this site
            </p>
            <div className="space-y-1.5">
              {activeJobs.map(j => (
                <div key={j.id} className="flex items-center justify-between text-sm bg-white rounded-lg px-3 py-1.5 border border-amber-100">
                  <span>
                    <span className="font-medium">{j.job_type.toLowerCase()}</span>
                    {' — '}{j.job_state.toLowerCase()}
                    {j.assigned_staff_name && <span className="text-gray-500"> ({j.assigned_staff_name})</span>}
                  </span>
                  <button
                    onClick={() => handleCancelJob(j.id)}
                    disabled={cancellingId === j.id}
                    className="text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                  >
                    {cancellingId === j.id ? 'Cancelling…' : 'Cancel'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {err && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-600 mb-1">Job Type</label>
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={jobType}
            onChange={e => setJobType(e.target.value)}
          >
            <option value="" disabled>— Select —</option>
            {JOB_TYPES.map(t => <option key={t} value={t}>{t.toLowerCase()}</option>)}
          </select>
        </div>
        <div className="mb-5">
          <label className="block text-xs font-medium text-gray-600 mb-1">Select Staff</label>
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={staffId}
            onChange={e => setStaffId(e.target.value)}
          >
            <option value="">Choose a staff member…</option>
            {activeStaff.map(p => (
              <option key={p.id} value={p.id}>
                {p.full_name || p.name}{p.districts?.length ? ` — ${p.districts.map(d => d.name).join(', ')}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleAssign}
            disabled={!staffId || !jobType || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60"
          >
            {saving ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OverrideTrackModal({ site, initialField, onClose, onUpdated }) {
  const [field, setField] = useState(initialField || 'site_status');
  const [newValue, setNewValue] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const locLabel = [site.district_name, site.taluk, site.gp].filter(Boolean).join(' / ');

  const isSiteStatus = field === 'site_status';
  const options = (isSiteStatus ? SITE_STATUSES : MTL_STATUSES)
    .filter(s => s !== (isSiteStatus ? site.site_status : site.mtl_status));

  const handleFieldChange = v => { setField(v); setNewValue(''); };

  const handleUpdate = async () => {
    if (!newValue || !reason.trim()) return;
    setSaving(true);
    setErr(null);
    try {
      const r = await apiFetch(`${API}/sites/${site.id}/override-track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ field, new_value: newValue, reason: reason.trim() }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      onUpdated();
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Override</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <div className="mb-4 bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
          <p className="text-xs font-medium text-gray-500 mb-1">📍 Site</p>
          <p className="text-sm font-semibold text-gray-800">{locLabel || site.govt_site_id || '—'}</p>
          {site.govt_site_id && locLabel && (
            <p className="text-xs text-gray-400 font-mono mt-0.5">{site.govt_site_id}</p>
          )}
        </div>

        {err && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}

        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-600 mb-1">Field</label>
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={field}
            onChange={e => handleFieldChange(e.target.value)}
          >
            <option value="site_status">Site Status</option>
            <option value="mtl_status">Materials</option>
          </select>
        </div>

        <div className="mb-4 flex items-center gap-2">
          <span className="text-xs text-gray-500 shrink-0">Current:</span>
          {isSiteStatus
            ? <SiteStatusBadge status={site.site_status} />
            : <MtlStatusBadge status={site.mtl_status} />}
        </div>

        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Set {isSiteStatus ? 'Site Status' : 'Materials'} To
          </label>
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={newValue}
            onChange={e => setNewValue(e.target.value)}
          >
            <option value="" disabled>— Select —</option>
            {options.map(s => (
              <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>

        <div className="mb-5">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Reason <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Re-surveying after site relocation"
          />
        </div>

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleUpdate}
            disabled={!newValue || !reason.trim() || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Override'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteConfirmModal({ site, onConfirm, onCancel }) {
  const [deleting, setDeleting] = useState(false);
  const handleConfirm = async () => {
    setDeleting(true);
    await onConfirm();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">Delete Site?</h2>
        <p className="text-sm text-gray-600 mb-5">
          This will permanently delete&nbsp;
          <span className="font-mono font-semibold text-gray-800">{site.site_code}</span>.
          This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={deleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-60"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}


function ActionModal({ site, actionDef, onClose, onDone }) {
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const locLabel = [site.district_name, site.taluk, site.gp].filter(Boolean).join(' / ');

  const handleConfirm = async () => {
    setSaving(true);
    setErr(null);
    try {
      const r = await apiFetch(`${API}/sites/${site.id}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: actionDef.action, notes: notes || null }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      onDone();
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">{actionDef.label.replace(/^[→⏸⊘]\s*/, '')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="mb-4 bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
          <p className="text-xs font-medium text-gray-500 mb-1">📍 Site</p>
          <p className="text-sm font-semibold text-gray-800">{locLabel || site.govt_site_id || '—'}</p>
          {site.govt_site_id && locLabel && (
            <p className="text-xs text-gray-400 font-mono mt-0.5">{site.govt_site_id}</p>
          )}
        </div>
        <div className="mb-4 flex items-center gap-2 text-xs text-gray-500">
          <InstallBadge status={site.install_status} />
          <span>→</span>
          <span className="font-medium text-blue-700">{actionDef.action.replace(/_/g, ' ')}</span>
        </div>
        {err && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}
        <div className="mb-5">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Notes <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="e.g. materials loaded, dispatch scheduled"
          />
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleConfirm}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60"
          >
            {saving ? 'Confirming…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PhotoLightbox({ photos, initialIndex, onClose }) {
  const [idx, setIdx] = useState(initialIndex);
  // photos already pre-filtered to those with URLs before being passed in

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && idx < photos.length - 1) setIdx(i => i + 1);
      else if (e.key === 'ArrowLeft' && idx > 0) setIdx(i => i - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photos.length, idx, onClose]);

  if (!photos.length) return null;
  const photo = photos[idx];

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 60 }}
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black bg-opacity-90" />

      <div className="absolute top-4 left-0 right-0 flex justify-between items-center px-5" style={{ zIndex: 1 }}>
        {photos.length > 1
          ? <span className="text-white text-sm font-medium opacity-80">{idx + 1} of {photos.length}</span>
          : <span />}
        <button onClick={onClose} className="text-white text-3xl leading-none opacity-70 hover:opacity-100">&times;</button>
      </div>

      <img
        src={photo.url}
        alt={photo.photo_type}
        className="relative object-contain rounded-lg shadow-2xl"
        style={{ zIndex: 1, maxWidth: '90vw', maxHeight: '85vh' }}
        onClick={e => e.stopPropagation()}
      />

      <div className="absolute bottom-4 left-0 right-0 text-center" style={{ zIndex: 1 }}>
        <span className="text-white text-xs opacity-60 uppercase tracking-wider">{photo.photo_type}</span>
      </div>

      {idx > 0 && (
        <button
          className="absolute left-2 top-1/2 -translate-y-1/2 text-white opacity-70 hover:opacity-100 px-3 py-4 text-5xl font-thin leading-none"
          style={{ zIndex: 1 }}
          onClick={e => { e.stopPropagation(); setIdx(i => i - 1); }}
        >‹</button>
      )}
      {idx < photos.length - 1 && (
        <button
          className="absolute right-2 top-1/2 -translate-y-1/2 text-white opacity-70 hover:opacity-100 px-3 py-4 text-5xl font-thin leading-none"
          style={{ zIndex: 1 }}
          onClick={e => { e.stopPropagation(); setIdx(i => i + 1); }}
        >›</button>
      )}
    </div>
  );
}

function SurveyDetailModal({ site, onClose, surveyedSites, surveyIndex, onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);
  const [error, setError] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    setData(null);
    setLoading(true);
    setNoData(false);
    setError(null);
    setLightboxIdx(null);
    apiFetch(`${API}/survey/data/${site.id}`)
      .then(r => {
        if (r.status === 404) { setNoData(true); setLoading(false); return null; }
        if (!r.ok) return r.json().then(j => { throw new Error(j.detail || `HTTP ${r.status}`); });
        return r.json();
      })
      .then(d => { if (d) { setData(d); setLoading(false); } })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [site.id]);

  const locLabel = [site.district_name, site.taluk, site.gp].filter(Boolean).join(' / ');
  const multiSite = surveyedSites.length > 1;
  const hasPrev = surveyIndex > 0;
  const hasNext = surveyIndex < surveyedSites.length - 1;

  // Pre-filter photos to only those with a usable URL — indices used for lightbox
  const photosWithUrl = data?.photos?.filter(p => p.url) ?? [];

  const SignalBadge = ({ v }) => {
    if (!v) return <span className="text-gray-400">—</span>;
    const cls = v === 'GOOD' ? 'bg-green-100 text-green-800' : v === 'WEAK' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-700';
    return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{v}</span>;
  };

  const Bool = ({ v }) => v
    ? <span className="text-green-600 font-medium">Yes</span>
    : <span className="text-red-500 font-medium">No</span>;

  const Row = ({ label, children }) => (
    <div className="flex py-2 border-b border-gray-100 last:border-0">
      <span className="text-xs font-medium text-gray-500 w-36 shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-gray-800 flex-1">{children}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-6">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6 my-2">

        {/* Header with site navigation */}
        <div className="flex items-start gap-2 mb-5">
          {multiSite && hasPrev ? (
            <button
              onClick={() => onNavigate(surveyIndex - 1)}
              className="mt-1 text-2xl leading-none text-gray-400 hover:text-gray-700 shrink-0"
              title="Previous surveyed site"
            >‹</button>
          ) : (
            multiSite && <span className="mt-1 w-5 shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-800">Site Detail</h2>
            <p className="text-sm text-gray-500 mt-0.5 truncate">{locLabel || site.govt_site_id || '—'}</p>
            {multiSite && (
              <p className="text-xs text-gray-400 mt-0.5">
                Site {surveyIndex + 1} of {surveyedSites.length} surveyed
              </p>
            )}
          </div>
          {multiSite && hasNext ? (
            <button
              onClick={() => onNavigate(surveyIndex + 1)}
              className="mt-1 text-2xl leading-none text-gray-400 hover:text-gray-700 shrink-0"
              title="Next surveyed site"
            >›</button>
          ) : (
            multiSite && <span className="mt-1 w-5 shrink-0" />
          )}
          <button onClick={onClose} className="mt-1 text-gray-400 hover:text-gray-600 text-2xl leading-none shrink-0 ml-1">&times;</button>
        </div>

        {loading && <Spinner />}

        {error && <ErrorMsg msg={error} />}

        {noData && (
          <div className="text-center py-10 text-gray-400">
            <div className="text-4xl mb-3">📋</div>
            <p className="text-sm">No survey data found for this site yet.</p>
          </div>
        )}

        {data && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <Row label="Site">{locLabel || '—'}</Row>
              <Row label="Surveyed At">
                {data.surveyed_at ? new Date(data.surveyed_at).toLocaleString('en-IN') : '—'}
              </Row>
              <Row label="GPS">
                {data.latitude && data.longitude ? (
                  <a
                    href={`https://www.google.com/maps?q=${data.latitude},${data.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {Number(data.latitude).toFixed(5)}, {Number(data.longitude).toFixed(5)} ↗
                  </a>
                ) : '—'}
              </Row>
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Network</p>
              <Row label="Airtel"><SignalBadge v={data.airtel_signal} /></Row>
              <Row label="BSNL"><SignalBadge v={data.bsnl_signal} /></Row>
              <Row label="Jio"><SignalBadge v={data.jio_signal} /></Row>
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Contacts</p>
              <Row label="Village Accountant">
                {data.va_name || '—'}{data.va_phone ? <span className="text-gray-500 ml-1">· {data.va_phone}</span> : ''}
              </Row>
              <Row label="Helper">
                {data.helper_name
                  ? <>{data.helper_name}{data.helper_phone ? <span className="text-gray-500 ml-1">· {data.helper_phone}</span> : ''}</>
                  : <span className="text-gray-400">—</span>}
              </Row>
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Site Assessment</p>
              <Row label="Installation">{data.installation_type || '—'}</Row>
              {data.installation_type === 'ROOFTOP' && data.rooftop_position && (
                <Row label="Rooftop Position">{data.rooftop_position}</Row>
              )}
              <Row label="Trees Nearby"><Bool v={data.trees_nearby} /></Row>
              <Row label="Bushes"><Bool v={data.bushes_on_site} /></Row>
              <Row label="Materials Available">
                <Bool v={data.materials_available} />
                {data.materials_remarks && <span className="text-gray-600 ml-1">— {data.materials_remarks}</span>}
              </Row>
              <Row label="Contractor">
                <Bool v={data.contractor_available} />
                {data.contractor_name && <span className="text-gray-600 ml-1">— {data.contractor_name}</span>}
                {data.contractor_phone && <span className="text-gray-500 ml-1">· {data.contractor_phone}</span>}
              </Row>
            </div>

            {data.remarks && (
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Remarks</p>
                <p className="text-sm text-gray-700">{data.remarks}</p>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Photos</p>
              {photosWithUrl.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No photos uploaded for this survey.</p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {photosWithUrl.map((p, i) => (
                    <button key={p.id || i} className="block group text-left" onClick={() => setLightboxIdx(i)}>
                      <img
                        src={p.url}
                        alt={p.photo_type}
                        className="w-full aspect-square object-cover rounded-lg border border-gray-200 group-hover:opacity-80 transition-opacity"
                      />
                      <p className="text-xs text-center text-gray-500 mt-1">{p.photo_type}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {lightboxIdx !== null && (
        <PhotoLightbox
          photos={photosWithUrl}
          initialIndex={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Installation Reports (read-only viewer)
// ---------------------------------------------------------------------------

function InstallationDetailModal({ visits, index, onClose, onNavigate, isAdmin, onUpdated }) {
  const summary = visits[index];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setData(null);
    setLoading(true);
    setError(null);
    setLightboxIdx(null);
    setEditing(false);
    apiFetch(`${API}/installation-visits/${summary.id}`)
      .then(r => {
        if (!r.ok) return r.json().then(j => { throw new Error(j.detail || `HTTP ${r.status}`); });
        return r.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [summary.id]);

  const hasPrev = index > 0;
  const hasNext = index < visits.length - 1;

  const allPhotos = data
    ? [
        ...(data.details.site_photo_url ? [{ url: data.details.site_photo_url, photo_type: 'Site' }] : []),
        ...data.photos.filter(p => p.url).map(p => ({ url: p.url, photo_type: p.angle })),
      ]
    : [];

  const headerLine1 = data
    ? [summary.district_name, summary.taluk, summary.gp].filter(Boolean).join(' / ')
      + (data.govt_site_id ? ` – ${data.govt_site_id}` : '')
    : '';

  const voltageRows = data
    ? [
        ['LFP cell voltage', data.details.stage1.no_load_voltage_lfp, data.details.stage2.under_load_voltage_lfp],
        ['Li-ion cell voltage', data.details.stage1.no_load_voltage_liion, data.details.stage2.under_load_voltage_liion],
        ['Solar Panel 1 voltage', data.details.stage1.no_load_voltage_solar1, data.details.stage2.under_load_voltage_solar1],
        ['Solar Panel 2 voltage', data.details.stage1.no_load_voltage_solar2, data.details.stage2.under_load_voltage_solar2],
      ]
    : [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-6">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-6xl mx-4 p-6 my-2">

        <div className="flex items-start gap-2 mb-5">
          {hasPrev && !editing ? (
            <button
              onClick={() => onNavigate(index - 1)}
              className="mt-1 text-2xl leading-none text-gray-400 hover:text-gray-700 shrink-0"
              title="Previous installation"
            >‹</button>
          ) : (
            <span className="mt-1 w-5 shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold text-gray-800">Installation Report</h2>
              {summary.gps_mismatch_flag && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-700">
                  ⚠ GPS MISMATCH
                </span>
              )}
              {isAdmin && data && !editing && (
                <button
                  onClick={() => setEditing(true)}
                  className="ml-auto px-2.5 py-1 text-xs font-medium bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Edit
                </button>
              )}
            </div>
            {data && (
              <>
                <p className="text-sm font-bold text-gray-800 mt-0.5 truncate">{headerLine1}</p>
                <p className="text-sm text-gray-700 mt-0.5">
                  <a
                    href={`https://www.google.com/maps?q=${data.gps_lat},${data.gps_lon}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {Number(data.gps_lat).toFixed(5)}, {Number(data.gps_lon).toFixed(5)} ↗
                  </a>
                </p>
                <p className="text-sm text-gray-700 mt-0.5">
                  Installed by {data.staff_name} on {new Date(data.visit_datetime).toLocaleDateString('en-IN')} at {new Date(data.visit_datetime).toLocaleTimeString('en-IN')}
                </p>
              </>
            )}
            <p className="text-xs text-gray-400 mt-0.5">Report {index + 1} of {visits.length}</p>
          </div>
          {hasNext && !editing ? (
            <button
              onClick={() => onNavigate(index + 1)}
              className="mt-1 text-2xl leading-none text-gray-400 hover:text-gray-700 shrink-0"
              title="Next installation"
            >›</button>
          ) : (
            <span className="mt-1 w-5 shrink-0" />
          )}
          <button onClick={onClose} className="mt-1 text-gray-400 hover:text-gray-600 text-2xl leading-none shrink-0 ml-1">&times;</button>
        </div>

        {loading && <Spinner />}
        {error && <ErrorMsg msg={error} />}

        {data && editing && (
          <InstallationVisitEditForm
            data={data}
            onCancel={() => setEditing(false)}
            onSaved={updated => { setData(updated); setEditing(false); onUpdated?.(); }}
          />
        )}

        {data && !editing && (
          <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Column 1: Parts / Remarks */}
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Parts</p>
                {data.parts.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No parts recorded.</p>
                ) : (
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr>
                        <th className="text-left text-xs font-medium text-gray-500 pb-1.5 border-b border-gray-200">Part</th>
                        <th className="text-left text-xs font-medium text-gray-500 pb-1.5 border-b border-gray-200">Part Number</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.parts.map(p => (
                        <tr key={p.id} className="border-b border-gray-100 last:border-0">
                          <td className="py-1.5 pr-2 text-gray-800">{p.material_name}</td>
                          <td className="py-1.5 text-gray-800">{p.part_no}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {data.details.remarks && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Remarks</p>
                  <p className="text-sm text-gray-700">{data.details.remarks}</p>
                </div>
              )}
            </div>

            {/* Column 2: Voltage table */}
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Voltage Readings</p>
                <table className="min-w-full text-sm">
                  <thead>
                    <tr>
                      <th className="text-left text-xs font-medium text-gray-500 pb-1.5 border-b border-gray-200"></th>
                      <th className="text-left text-xs font-medium text-gray-500 pb-1.5 border-b border-gray-200">Without DL</th>
                      <th className="text-left text-xs font-medium text-gray-500 pb-1.5 border-b border-gray-200">With DL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {voltageRows.map(([label, noLoad, underLoad]) => (
                      <tr key={label} className="border-b border-gray-100 last:border-0">
                        <td className="py-1.5 pr-2 text-xs font-medium text-gray-500">{label}</td>
                        <td className="py-1.5 pr-2 text-gray-800">{noLoad ?? '—'}</td>
                        <td className="py-1.5 text-gray-800">{underLoad ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Column 3: Photos */}
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Photos</p>
                {allPhotos.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No photos uploaded.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {allPhotos.map((p, i) => (
                      <button key={i} className="block group text-left" onClick={() => setLightboxIdx(i)}>
                        <img
                          src={p.url}
                          alt={p.photo_type}
                          className="w-full aspect-square object-cover rounded-lg border border-gray-200 group-hover:opacity-80 transition-opacity"
                        />
                        <p className="text-xs text-center text-gray-500 mt-1">{p.photo_type}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-4 bg-gray-50 rounded-xl p-4 flex flex-wrap gap-x-6 gap-y-1 justify-center text-sm text-gray-700">
            <span>Manual tipping: {data.details.stage2.manual_tip_check_ok ? 'Ok' : '—'}</span>
            <span>Signal Strength: {data.details.stage2.signal_strength ?? '—'}</span>
            <span>Calibration: {data.details.stage3.final_calibration_pass ? 'Ok' : '—'}</span>
            <span>Data Logging: {data.details.stage3.data_logging_confirmed ? 'Ok' : '—'}</span>
            <span>Health message: {data.details.health_sms_sent ? 'sent' : '—'}</span>
            <span>Mobile Number: {data.details.stage1.mobile_number || '—'}</span>
          </div>
          </>
        )}
      </div>

      {lightboxIdx !== null && (
        <PhotoLightbox
          photos={allPhotos}
          initialIndex={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </div>
  );
}

const _INSTALL_ANGLES = ['North', 'East', 'South', 'West'];

// One photo tile in the edit form: shows the current (or newly-picked) photo,
// with Replace (pick a new file) and Remove/Undo. `edit` is { file, remove } —
// file set means "replace with this", remove:true means "clear this slot".
function PhotoEditSlot({ label, currentUrl, edit, onChange }) {
  const fileInputRef = useRef(null);
  const previewUrl = useMemo(() => (edit.file ? URL.createObjectURL(edit.file) : null), [edit.file]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const displayUrl = edit.remove ? null : (previewUrl || currentUrl);

  return (
    <div>
      <div className="aspect-square rounded-lg border border-gray-200 bg-gray-100 overflow-hidden flex items-center justify-center">
        {displayUrl ? (
          <img src={displayUrl} alt={label} className="w-full h-full object-cover" />
        ) : (
          <span className="text-xs text-gray-400">{edit.remove ? 'Removed' : 'No photo'}</span>
        )}
      </div>
      <p className="text-xs text-center text-gray-500 mt-1">{label}</p>
      <div className="flex gap-1 mt-1 justify-center">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={e => {
            const f = e.target.files?.[0] || null;
            if (f) onChange({ file: f, remove: false });
            e.target.value = '';
          }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
        >
          Replace
        </button>
        {edit.remove ? (
          <button
            type="button"
            onClick={() => onChange({ file: null, remove: false })}
            className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
          >
            Undo
          </button>
        ) : (
          (currentUrl || edit.file) && (
            <button
              type="button"
              onClick={() => onChange({ file: null, remove: true })}
              className="text-xs px-2 py-0.5 rounded bg-red-50 text-red-600 hover:bg-red-100"
            >
              Remove
            </button>
          )
        )}
      </div>
    </div>
  );
}

// ADMIN-only inline edit for an Installation Report. Covers every field
// including GPS (identical dual-write to Bulk Correct GPS, applied
// server-side), parts (plain overwritable text — no swap/consumption
// tracking here, that's a future Fault Attendance feature), and photos
// (replace/remove, also applied server-side against MinIO + the DB row).
function InstallationVisitEditForm({ data, onCancel, onSaved }) {
  const [materials, setMaterials] = useState(null);
  const [eGpsLat, setEGpsLat] = useState(String(data.gps_lat));
  const [eGpsLon, setEGpsLon] = useState(String(data.gps_lon));
  const [eParts, setEParts] = useState(() => {
    const m = {};
    for (const p of data.parts) m[p.material_id] = p.part_no;
    return m;
  });
  const [eStage1, setEStage1] = useState({
    sim_card_inserted: data.details.stage1.sim_card_inserted,
    mobile_number: data.details.stage1.mobile_number || '',
    no_load_voltage_lfp: data.details.stage1.no_load_voltage_lfp ?? '',
    no_load_voltage_liion: data.details.stage1.no_load_voltage_liion ?? '',
    no_load_voltage_solar1: data.details.stage1.no_load_voltage_solar1 ?? '',
    no_load_voltage_solar2: data.details.stage1.no_load_voltage_solar2 ?? '',
  });
  const [eStage2, setEStage2] = useState({
    wiring_rf_sensor_tipping_bucket: data.details.stage2.wiring_rf_sensor_tipping_bucket,
    wiring_gsm_antenna: data.details.stage2.wiring_gsm_antenna,
    wiring_solar_panel: data.details.stage2.wiring_solar_panel,
    wiring_datalogger_through_tester: data.details.stage2.wiring_datalogger_through_tester,
    under_load_voltage_lfp: data.details.stage2.under_load_voltage_lfp ?? '',
    under_load_voltage_liion: data.details.stage2.under_load_voltage_liion ?? '',
    under_load_voltage_solar1: data.details.stage2.under_load_voltage_solar1 ?? '',
    under_load_voltage_solar2: data.details.stage2.under_load_voltage_solar2 ?? '',
    power_up_ok: data.details.stage2.power_up_ok === true ? 'ok' : data.details.stage2.power_up_ok === false ? 'not_ok' : null,
    station_id_assigned: data.details.stage2.station_id_assigned,
    station_id: data.details.stage2.station_id || '',
    network_ok: data.details.stage2.network_ok === true ? 'ok' : data.details.stage2.network_ok === false ? 'not_ok' : null,
    signal_strength: data.details.stage2.signal_strength ?? '',
    manual_tip_check_ok: data.details.stage2.manual_tip_check_ok === true ? 'ok' : data.details.stage2.manual_tip_check_ok === false ? 'not_ok' : null,
    switched_off_tester_removed: data.details.stage2.switched_off_tester_removed,
  });
  const [eStage3, setEStage3] = useState({
    direct_connection_done: data.details.stage3.direct_connection_done,
    data_logging_confirmed: data.details.stage3.data_logging_confirmed,
    final_calibration_pass: data.details.stage3.final_calibration_pass === true ? 'ok' : data.details.stage3.final_calibration_pass === false ? 'not_ok' : null,
  });
  const [eHealthSms, setEHealthSms] = useState(data.details.health_sms_sent);
  const [eRemarks, setERemarks] = useState(data.details.remarks || '');
  const [photoEdits, setPhotoEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    apiFetch(`${API}/materials?active=true`)
      .then(r => (r.ok ? r.json() : []))
      .then(setMaterials)
      .catch(() => setMaterials([]));
  }, []);

  const activeMaterials = (materials || []).filter(m => m.name.trim().toLowerCase() !== 'sim card');

  const photosByAngle = useMemo(() => {
    const m = {};
    for (const p of data.photos) m[p.angle] = p;
    return m;
  }, [data.photos]);

  const photoEdit = key => photoEdits[key] || { file: null, remove: false };
  const setPhotoEdit = (key, next) => setPhotoEdits(prev => ({ ...prev, [key]: next }));

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const fd = new FormData();
      fd.append('gps_lat', eGpsLat);
      fd.append('gps_lon', eGpsLon);

      const partsPayload = activeMaterials.map(m => ({ material_id: m.id, part_no: (eParts[m.id] || '').trim() }));
      fd.append('parts_json', JSON.stringify(partsPayload));

      const toBool = v => (v === 'ok' ? true : v === 'not_ok' ? false : null);
      const toNum = v => (v === '' || v === null || v === undefined ? null : parseFloat(v));

      const details = {
        stage1: {
          sim_card_inserted: eStage1.sim_card_inserted,
          mobile_number: eStage1.mobile_number.trim() || null,
          no_load_voltage_lfp: toNum(eStage1.no_load_voltage_lfp),
          no_load_voltage_liion: toNum(eStage1.no_load_voltage_liion),
          no_load_voltage_solar1: toNum(eStage1.no_load_voltage_solar1),
          no_load_voltage_solar2: toNum(eStage1.no_load_voltage_solar2),
        },
        stage2: {
          wiring_rf_sensor_tipping_bucket: eStage2.wiring_rf_sensor_tipping_bucket,
          wiring_gsm_antenna: eStage2.wiring_gsm_antenna,
          wiring_solar_panel: eStage2.wiring_solar_panel,
          wiring_datalogger_through_tester: eStage2.wiring_datalogger_through_tester,
          under_load_voltage_lfp: toNum(eStage2.under_load_voltage_lfp),
          under_load_voltage_liion: toNum(eStage2.under_load_voltage_liion),
          under_load_voltage_solar1: toNum(eStage2.under_load_voltage_solar1),
          under_load_voltage_solar2: toNum(eStage2.under_load_voltage_solar2),
          power_up_ok: toBool(eStage2.power_up_ok),
          station_id_assigned: eStage2.station_id_assigned,
          station_id: eStage2.station_id_assigned ? (eStage2.station_id.trim() || null) : null,
          network_ok: toBool(eStage2.network_ok),
          signal_strength: toNum(eStage2.signal_strength),
          manual_tip_check_ok: toBool(eStage2.manual_tip_check_ok),
          switched_off_tester_removed: eStage2.switched_off_tester_removed,
        },
        stage3: {
          direct_connection_done: eStage3.direct_connection_done,
          data_logging_confirmed: eStage3.data_logging_confirmed,
          final_calibration_pass: toBool(eStage3.final_calibration_pass),
        },
        health_sms_sent: eHealthSms,
        remarks: eRemarks.trim() || null,
      };
      fd.append('details_json', JSON.stringify(details));

      for (const angle of _INSTALL_ANGLES) {
        const edit = photoEdit(angle);
        if (edit.remove) fd.append(`remove_photo_${angle.toLowerCase()}`, 'true');
        else if (edit.file) fd.append(`photo_${angle.toLowerCase()}`, edit.file, edit.file.name || `${angle}.jpg`);
      }
      const siteEdit = photoEdit('site');
      if (siteEdit.remove) fd.append('remove_site_photo', 'true');
      else if (siteEdit.file) fd.append('site_photo', siteEdit.file, siteEdit.file.name || 'site_photo.jpg');

      const r = await apiFetch(`${API}/installation-visits/${data.id}/admin-update`, { method: 'PUT', body: fd });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      onSaved(await r.json());
    } catch (e) {
      setSaveError(e.message);
      setSaving(false);
    }
  };

  const inputCls = 'w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Column 1: Parts / Remarks / GPS */}
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Parts</p>
            {materials === null ? (
              <Spinner />
            ) : activeMaterials.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No materials configured.</p>
            ) : (
              <div className="space-y-3">
                {activeMaterials.map(m => (
                  <div key={m.id}>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{m.name}</label>
                    <PartNumberInput
                      value={eParts[m.id] || ''}
                      prefix={m.part_prefix || ''}
                      onChange={v => setEParts(s => ({ ...s, [m.id]: v }))}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Remarks</p>
            <textarea
              className={`${inputCls} min-h-[80px]`}
              value={eRemarks}
              onChange={e => setERemarks(e.target.value)}
            />
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">GPS</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Latitude</label>
                <input className={inputCls} type="text" inputMode="decimal" value={eGpsLat} onChange={e => setEGpsLat(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Longitude</label>
                <input className={inputCls} type="text" inputMode="decimal" value={eGpsLon} onChange={e => setEGpsLon(e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        {/* Column 2: Stage 1/2/3 readings and checks */}
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Voltage Readings</p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-2">
              <p className="col-span-2 text-xs text-gray-400 -mb-1">Without datalogger</p>
              <InstallNumberInput label="LFP" value={eStage1.no_load_voltage_lfp} onChange={v => setEStage1(s => ({ ...s, no_load_voltage_lfp: v }))} />
              <InstallNumberInput label="Li-ion" value={eStage1.no_load_voltage_liion} onChange={v => setEStage1(s => ({ ...s, no_load_voltage_liion: v }))} />
              <InstallNumberInput label="Solar 1" value={eStage1.no_load_voltage_solar1} onChange={v => setEStage1(s => ({ ...s, no_load_voltage_solar1: v }))} />
              <InstallNumberInput label="Solar 2" value={eStage1.no_load_voltage_solar2} onChange={v => setEStage1(s => ({ ...s, no_load_voltage_solar2: v }))} />
              <p className="col-span-2 text-xs text-gray-400 -mb-1 mt-1">With datalogger</p>
              <InstallNumberInput label="LFP" value={eStage2.under_load_voltage_lfp} onChange={v => setEStage2(s => ({ ...s, under_load_voltage_lfp: v }))} />
              <InstallNumberInput label="Li-ion" value={eStage2.under_load_voltage_liion} onChange={v => setEStage2(s => ({ ...s, under_load_voltage_liion: v }))} />
              <InstallNumberInput label="Solar 1" value={eStage2.under_load_voltage_solar1} onChange={v => setEStage2(s => ({ ...s, under_load_voltage_solar1: v }))} />
              <InstallNumberInput label="Solar 2" value={eStage2.under_load_voltage_solar2} onChange={v => setEStage2(s => ({ ...s, under_load_voltage_solar2: v }))} />
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 space-y-1">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Wiring / Checks</p>
            <InstallCheckbox label="SIM card inserted" checked={eStage1.sim_card_inserted} onChange={v => setEStage1(s => ({ ...s, sim_card_inserted: v }))} />
            <InstallCheckbox label="RF sensor / tipping bucket wired" checked={eStage2.wiring_rf_sensor_tipping_bucket} onChange={v => setEStage2(s => ({ ...s, wiring_rf_sensor_tipping_bucket: v }))} />
            <InstallCheckbox label="GSM antenna wired" checked={eStage2.wiring_gsm_antenna} onChange={v => setEStage2(s => ({ ...s, wiring_gsm_antenna: v }))} />
            <InstallCheckbox label="Solar panel wired" checked={eStage2.wiring_solar_panel} onChange={v => setEStage2(s => ({ ...s, wiring_solar_panel: v }))} />
            <InstallCheckbox label="Datalogger through tester wired" checked={eStage2.wiring_datalogger_through_tester} onChange={v => setEStage2(s => ({ ...s, wiring_datalogger_through_tester: v }))} />
            <InstallCheckbox label="Switched-off tester removed" checked={eStage2.switched_off_tester_removed} onChange={v => setEStage2(s => ({ ...s, switched_off_tester_removed: v }))} />
            <InstallCheckbox label="Direct connection done" checked={eStage3.direct_connection_done} onChange={v => setEStage3(s => ({ ...s, direct_connection_done: v }))} />
            <InstallCheckbox label="Data logging confirmed" checked={eStage3.data_logging_confirmed} onChange={v => setEStage3(s => ({ ...s, data_logging_confirmed: v }))} />
            <InstallCheckbox label="Health SMS sent" checked={eHealthSms} onChange={setEHealthSms} />
          </div>

          <div className="bg-gray-50 rounded-xl p-4 space-y-1">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Pass / Fail</p>
            <InstallToggle label="Power-up" value={eStage2.power_up_ok} onChange={v => setEStage2(s => ({ ...s, power_up_ok: v }))} />
            <InstallToggle label="Network" value={eStage2.network_ok} onChange={v => setEStage2(s => ({ ...s, network_ok: v }))} />
            <InstallToggle label="Manual tip check" value={eStage2.manual_tip_check_ok} onChange={v => setEStage2(s => ({ ...s, manual_tip_check_ok: v }))} />
            <InstallToggle label="Final calibration" value={eStage3.final_calibration_pass} onChange={v => setEStage3(s => ({ ...s, final_calibration_pass: v }))} trueLabel="Pass" falseLabel="Fail" />
          </div>

          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Station / Network</p>
            <InstallCheckbox label="Station ID assigned" checked={eStage2.station_id_assigned} onChange={v => setEStage2(s => ({ ...s, station_id_assigned: v }))} />
            {eStage2.station_id_assigned && (
              <InstallTextInput label="Station ID" value={eStage2.station_id} onChange={v => setEStage2(s => ({ ...s, station_id: v }))} />
            )}
            <InstallNumberInput label="Signal Strength" value={eStage2.signal_strength} onChange={v => setEStage2(s => ({ ...s, signal_strength: v }))} />
            <InstallTextInput label="Mobile Number" value={eStage1.mobile_number} onChange={v => setEStage1(s => ({ ...s, mobile_number: v }))} placeholder="Optional" />
          </div>
        </div>

        {/* Column 3: Photos */}
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Photos</p>
            <div className="grid grid-cols-3 gap-3">
              <PhotoEditSlot
                label="Site"
                currentUrl={data.details.site_photo_url}
                edit={photoEdit('site')}
                onChange={next => setPhotoEdit('site', next)}
              />
              {_INSTALL_ANGLES.map(angle => (
                <PhotoEditSlot
                  key={angle}
                  label={angle}
                  currentUrl={photosByAngle[angle]?.url || null}
                  edit={photoEdit(angle)}
                  onChange={next => setPhotoEdit(angle, next)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {saveError && <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{saveError}</div>}

      <div className="mt-4 flex justify-end gap-3">
        <button onClick={onCancel} disabled={saving} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-60">
          Cancel
        </button>
        <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </>
  );
}

function InstallationReports({ role }) {
  const isAdmin = role === 'ADMIN';
  const canManage = role === 'ADMIN' || role === 'SUPERVISOR';
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailIdx, setDetailIdx] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    apiFetch(`${API}/installation-visits`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => { setVisits(d); setSelected(new Set()); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const r = await apiFetch(`${API}/installation-visits/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visit_ids: [...selected] }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'installations_export.zip';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(e.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Installation Reports</h1>
      </div>

      {loading && <Spinner />}
      {error && <ErrorMsg msg={error} />}
      {!loading && !error && visits.length === 0 && <EmptyState label="installation reports" />}
      {!loading && !error && visits.length > 0 && (
        <>
          {canManage && (
            <div
              className={`flex flex-wrap items-center gap-3 rounded-lg px-4 py-2.5 mb-3 border ${
                selected.size > 0 ? 'bg-blue-50 border-blue-200' : 'invisible border-transparent'
              }`}
            >
              <span className="text-sm font-medium text-blue-800">{selected.size} selected</span>
              <button onClick={() => setSelected(new Set())} className="text-xs text-blue-600 hover:underline">Clear</button>
              {exportError && <span className="text-xs text-red-600">{exportError}</span>}
              <div className="ml-auto flex gap-2">
                <button
                  onClick={handleExport}
                  disabled={exporting}
                  className="px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {exporting ? 'Exporting…' : 'Export Selected'}
                </button>
                {isAdmin && (
                  <button
                    onClick={() => setBulkDeleteOpen(true)}
                    className="px-3 py-1.5 text-sm font-medium bg-white border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                  >
                    Delete Selected
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="bg-white rounded-xl shadow overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {canManage && (
                    <th className="px-4 py-3 text-left w-8">
                      <input
                        type="checkbox"
                        checked={visits.length > 0 && visits.every(v => selected.has(v.id))}
                        onChange={e => {
                          setSelected(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) visits.forEach(v => next.add(v.id));
                            else visits.forEach(v => next.delete(v.id));
                            return next;
                          });
                        }}
                      />
                    </th>
                  )}
                  {['Site', 'District / Taluk / GP', 'Staff', 'Visit Date', 'Calibration', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visits.map((v, i) => (
                  <tr key={v.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setDetailIdx(i)}>
                    {canManage && (
                      <td className="px-4 py-3 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(v.id)}
                          onChange={e => {
                            setSelected(prev => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(v.id);
                              else next.delete(v.id);
                              return next;
                            });
                          }}
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="font-mono text-xs font-medium text-blue-600 block">{v.govt_site_id || v.site_code}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {[v.district_name, v.taluk, v.gp].filter(Boolean).join(' / ') || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{v.staff_name}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {new Date(v.visit_datetime).toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {v.calibration_result === true && (
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Pass</span>
                      )}
                      {v.calibration_result === false && (
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">Fail</span>
                      )}
                      {v.calibration_result == null && <span className="text-gray-400 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {v.gps_mismatch_flag && (
                        <span
                          title="GPS mismatch on submission"
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700"
                        >
                          ⚠ GPS
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {detailIdx !== null && (
        <InstallationDetailModal
          visits={visits}
          index={detailIdx}
          onClose={() => setDetailIdx(null)}
          onNavigate={setDetailIdx}
          isAdmin={isAdmin}
          onUpdated={load}
        />
      )}

      {bulkDeleteOpen && (
        <BulkDeleteInstallationVisitsModal
          visitIds={[...selected]}
          onClose={() => setBulkDeleteOpen(false)}
          onDone={() => { setBulkDeleteOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function BulkGpsCorrectionModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const handleSubmit = async () => {
    if (!file) return;
    setSaving(true);
    setErr(null);
    try {
      const csvText = await file.text();
      const r = await apiFetch(`${API}/installation-visits/bulk-gps-correction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv_text: csvText }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      onDone(await r.json());
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  const codeCls = 'font-mono text-xs bg-gray-100 px-1 rounded';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Bulk Correct GPS</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <p className="text-sm text-gray-500 mb-3">
          Upload a .csv file with columns <span className={codeCls}>govt_site_id</span>,{' '}
          <span className={codeCls}>gps_lat</span>, <span className={codeCls}>gps_lon</span> (all required per row).
          Corrects the GPS on that site's most recent installation report and updates the site's
          government-provided coordinates to match — nothing else on the report is changed.
        </p>
        {err && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={e => setFile(e.target.files?.[0] || null)}
          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 mb-5 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-blue-50 file:text-blue-700 file:text-sm file:font-medium hover:file:bg-blue-100"
        />
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={!file || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60"
          >
            {saving ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkGpsCorrectionResultModal({ result, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Bulk Correct GPS — Result</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="mb-4 flex gap-4 text-sm flex-wrap">
          <span className="text-green-700 font-medium">✓ {result.updated.length} updated</span>
          <span className="text-amber-700 font-medium">⚠ {result.skipped_not_found.length} not found</span>
          <span className="text-red-700 font-medium">✗ {result.skipped_invalid.length} invalid</span>
        </div>
        {result.updated.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Updated</p>
            <div className="space-y-1.5 bg-green-50 rounded-lg p-3 border border-green-200">
              {result.updated.map((u, i) => (
                <div key={`${u.govt_site_id}-${i}`} className="text-sm text-gray-700">
                  <span className="font-mono text-xs text-gray-500">{u.govt_site_id}</span>: {u.gps_lat}, {u.gps_lon}
                </div>
              ))}
            </div>
          </div>
        )}
        {result.skipped_not_found.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Not Found</p>
            <div className="space-y-1.5 bg-amber-50 rounded-lg p-3 border border-amber-200">
              {result.skipped_not_found.map((s, i) => (
                <div key={`${s.govt_site_id}-${i}`} className="text-sm text-gray-700">
                  <span className="font-mono text-xs text-gray-500">{s.govt_site_id}</span>: {s.reason}
                </div>
              ))}
            </div>
          </div>
        )}
        {result.skipped_invalid.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Invalid</p>
            <div className="space-y-1.5 bg-red-50 rounded-lg p-3 border border-red-200">
              {result.skipped_invalid.map((s, i) => (
                <div key={`${s.govt_site_id}-${i}`} className="text-sm text-gray-700">
                  <span className="font-mono text-xs text-gray-500">{s.govt_site_id || '(missing id)'}</span>: {s.reason}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">Close</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bulk Tools — sidebar page hosting the CSV-driven bulk actions that don't
// depend on a row selection (Import Faults, Site Master bulk CSV update,
// Bulk Correct GPS). Route/nav are gated to ADMIN/SUPERVISOR; the modals
// themselves are defined elsewhere in this file and reused here as-is.
// ---------------------------------------------------------------------------

function BulkTools() {
  const [importFaultsOpen, setImportFaultsOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [siteDetailsCsvOpen, setSiteDetailsCsvOpen] = useState(false);
  const [siteDetailsCsvResult, setSiteDetailsCsvResult] = useState(null);
  const [bulkGpsOpen, setBulkGpsOpen] = useState(false);
  const [bulkGpsResult, setBulkGpsResult] = useState(null);

  const tools = [
    {
      key: 'import-faults',
      icon: '⚠',
      title: 'Import Faults',
      description: 'Upload a CSV of government-reported site IDs to bulk-mark matching sites as FAULT.',
      onClick: () => setImportFaultsOpen(true),
    },
    {
      key: 'site-master-csv',
      icon: '📍',
      title: 'Bulk Update Site Details',
      description: 'Upload a CSV to update GPS coordinates and/or helper contact details for multiple sites at once.',
      onClick: () => setSiteDetailsCsvOpen(true),
    },
    {
      key: 'gps-correction',
      icon: '🛠️',
      title: 'Bulk Correct GPS',
      description: "Upload a CSV to correct GPS on already-submitted Installation Reports and sync the site's official coordinates.",
      onClick: () => setBulkGpsOpen(true),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Bulk Tools</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {tools.map(t => (
          <button
            key={t.key}
            onClick={t.onClick}
            className="text-left bg-white rounded-xl shadow p-5 hover:shadow-md hover:ring-2 hover:ring-blue-200 transition"
          >
            <div className="text-2xl mb-2">{t.icon}</div>
            <div className="font-semibold text-gray-800 mb-1">{t.title}</div>
            <div className="text-sm text-gray-500">{t.description}</div>
          </button>
        ))}
      </div>

      {importFaultsOpen && (
        <ImportFaultsModal
          onClose={() => setImportFaultsOpen(false)}
          onDone={result => { setImportFaultsOpen(false); setImportResult(result); }}
        />
      )}
      {importResult && (
        <ImportFaultsResultModal result={importResult} onClose={() => setImportResult(null)} />
      )}

      {siteDetailsCsvOpen && (
        <BulkSiteDetailsCsvModal
          onClose={() => setSiteDetailsCsvOpen(false)}
          onDone={result => { setSiteDetailsCsvOpen(false); setSiteDetailsCsvResult(result); }}
        />
      )}
      {siteDetailsCsvResult && (
        <BulkSiteDetailsCsvResultModal result={siteDetailsCsvResult} onClose={() => setSiteDetailsCsvResult(null)} />
      )}

      {bulkGpsOpen && (
        <BulkGpsCorrectionModal
          onClose={() => setBulkGpsOpen(false)}
          onDone={result => { setBulkGpsOpen(false); setBulkGpsResult(result); }}
        />
      )}
      {bulkGpsResult && (
        <BulkGpsCorrectionResultModal result={bulkGpsResult} onClose={() => setBulkGpsResult(null)} />
      )}
    </div>
  );
}

function BulkDeleteInstallationVisitsModal({ visitIds, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const handleConfirm = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await apiFetch(`${API}/installation-visits/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visit_ids: visitIds }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      onDone();
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">
          Delete {visitIds.length} Installation Report{visitIds.length !== 1 ? 's' : ''}?
        </h2>
        <p className="text-sm text-gray-600 mb-5">
          This permanently deletes the selected installation reports — including their photos —
          from the system. The site's install status and job history are not affected.
          This action cannot be undone.
        </p>
        {err && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleConfirm}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-60"
          >
            {busy ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bulk operations (Phase B)
// ---------------------------------------------------------------------------

function BulkStatusModal({ siteIds, onClose, onDone }) {
  const [status, setStatus] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const handleSubmit = async () => {
    if (!status || !reason.trim()) return;
    setSaving(true);
    setErr(null);
    try {
      const r = await apiFetch(`${API}/sites/bulk/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_ids: siteIds, site_status: status, reason: reason.trim() }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      onDone(await r.json());
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Set Status</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <p className="text-sm text-gray-500 mb-4">{siteIds.length} site{siteIds.length !== 1 ? 's' : ''} selected</p>
        {err && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-600 mb-1">Site Status</label>
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={status}
            onChange={e => setStatus(e.target.value)}
          >
            <option value="" disabled>— Select —</option>
            {SITE_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
        <div className="mb-5">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Reason <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Govt fault report"
          />
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={!status || !reason.trim() || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60"
          >
            {saving ? 'Applying…' : `Apply to ${siteIds.length} site${siteIds.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkAssignJobsModal({ siteIds, personnel, onClose, onDone }) {
  const [jobType, setJobType] = useState('');
  const [mode, setMode] = useState('auto');
  const [staffId, setStaffId] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const activeStaff = (personnel || []).filter(p => p.is_active);

  const handleSubmit = async () => {
    if (!jobType) return;
    if (mode === 'manual' && !staffId) return;
    setSaving(true);
    setErr(null);
    try {
      const body = { site_ids: siteIds, job_type: jobType, mode };
      if (mode === 'manual') body.staff_id = staffId;
      const r = await apiFetch(`${API}/sites/bulk/assign-jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      onDone(await r.json());
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  const toggleCls = active =>
    `flex-1 py-2 font-medium ${active ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Assign Jobs</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <p className="text-sm text-gray-500 mb-4">{siteIds.length} site{siteIds.length !== 1 ? 's' : ''} selected</p>
        {err && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-600 mb-1">Job Type</label>
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={jobType}
            onChange={e => setJobType(e.target.value)}
          >
            <option value="" disabled>— Select —</option>
            {JOB_TYPES.map(t => <option key={t} value={t}>{t.toLowerCase()}</option>)}
          </select>
        </div>
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-600 mb-1">Mode</label>
          <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
            <button type="button" onClick={() => setMode('auto')} className={toggleCls(mode === 'auto')}>
              Auto by district
            </button>
            <button
              type="button"
              onClick={() => setMode('manual')}
              className={`${toggleCls(mode === 'manual')} border-l border-gray-300`}
            >
              Manual staff pick
            </button>
          </div>
        </div>
        {mode === 'manual' ? (
          <div className="mb-5">
            <label className="block text-xs font-medium text-gray-600 mb-1">Select Staff</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={staffId}
              onChange={e => setStaffId(e.target.value)}
            >
              <option value="">Choose a staff member…</option>
              {activeStaff.map(p => (
                <option key={p.id} value={p.id}>{p.full_name || p.name}</option>
              ))}
            </select>
          </div>
        ) : (
          <p className="mb-5 text-xs text-gray-400">
            Each site is auto-assigned to the one active staff member linked to its district.
            Sites with zero or multiple matching staff are skipped and reported.
          </p>
        )}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={!jobType || (mode === 'manual' && !staffId) || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60"
          >
            {saving ? 'Assigning…' : `Assign ${siteIds.length} site${siteIds.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkAssignResultModal({ result, sitesById, onClose }) {
  const label = item => {
    const site = sitesById[item.site_id];
    if (!site) return item.site_id.slice(0, 8);
    return site.govt_site_id || [site.district_name, site.taluk, site.gp].filter(Boolean).join(' / ') || site.site_code;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Assign Jobs — Result</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="mb-4 flex gap-4 text-sm">
          <span className="text-green-700 font-medium">✓ {result.assigned.length} assigned</span>
          <span className="text-amber-700 font-medium">⚠ {result.skipped.length} skipped</span>
        </div>
        {result.assigned.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Assigned</p>
            <div className="space-y-1.5 bg-green-50 rounded-lg p-3 border border-green-200">
              {result.assigned.map(a => (
                <div key={a.site_id} className="text-sm text-gray-700">
                  <span className="font-mono text-xs text-gray-500">{label(a)}</span> → {a.staff_name}
                </div>
              ))}
            </div>
          </div>
        )}
        {result.skipped.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Skipped</p>
            <div className="space-y-1.5 bg-amber-50 rounded-lg p-3 border border-amber-200">
              {result.skipped.map(sres => (
                <div key={sres.site_id} className="text-sm text-gray-700">
                  <span className="font-mono text-xs text-gray-500">{label(sres)}</span>: {sres.reason}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">Close</button>
        </div>
      </div>
    </div>
  );
}

// Superseded by MaterialRequestModal below (itemized line-item flow, Slice
// 2) — the underlying endpoint (POST /sites/bulk/request-materials) and its
// data are left untouched and still reachable server-side.
function MaterialRequestModal({ siteIds, sitesById, onClose, onDone }) {
  const { data: materials } = useFetch(`${API}/materials?active=true`);
  const [lines, setLines] = useState([{ material_id: '', name: '', quantity: 1 }]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const materialByName = useMemo(
    () => Object.fromEntries((materials || []).map(m => [m.name, m])),
    [materials]
  );

  const siteLabel = id => {
    const site = sitesById[id];
    if (!site) return id.slice(0, 8);
    return site.govt_site_id || [site.district_name, site.taluk, site.gp].filter(Boolean).join(' / ') || site.site_code;
  };

  const updateLine = (idx, patch) => setLines(ls => ls.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const addLine = () => setLines(ls => [...ls, { material_id: '', name: '', quantity: 1 }]);
  const removeLine = idx => setLines(ls => ls.filter((_, i) => i !== idx));

  const validLines = lines.filter(l => l.material_id && Number(l.quantity) > 0);
  const canSubmit = validLines.length > 0 && validLines.length === lines.length && !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setErr(null);
    try {
      const r = await apiFetch(`${API}/material-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_ids: siteIds,
          lines: validLines.map(l => ({ material_id: l.material_id, quantity: Number(l.quantity) })),
          ...(notes.trim() && { notes: notes.trim() }),
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      onDone(await r.json());
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  // No w-full here — this component applies flex-sizing utilities
  // (flex-1, w-16) on top of inputCls for the material/quantity row, and
  // w-full at the same specificity as those wins the cascade regardless of
  // class order in the string, collapsing both inputs to full width. Callers
  // that do want full width (e.g. the notes textarea below) add w-full
  // explicitly at the call site instead.
  const inputCls = 'border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-8">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Request Materials</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
            {siteIds.length} site{siteIds.length !== 1 ? 's' : ''} selected
          </p>
          <div className="max-h-20 overflow-y-auto flex flex-wrap gap-1.5 bg-gray-50 rounded-lg p-2 border border-gray-200">
            {siteIds.map(id => (
              <span key={id} className="text-xs font-mono text-gray-600 bg-white border border-gray-200 rounded px-1.5 py-0.5">
                {siteLabel(id)}
              </span>
            ))}
          </div>
        </div>

        {err && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}

        <div className="mb-4 space-y-2">
          <label className="block text-xs font-medium text-gray-600">Materials</label>
          {lines.map((line, idx) => {
            const qty = Number(line.quantity) || 0;
            const total = qty * siteIds.length;
            return (
              <div key={idx}>
                <div className="flex items-center gap-2">
                  <input
                    list="material-request-options"
                    className={`${inputCls} flex-1 min-w-0`}
                    value={line.name}
                    placeholder="Search material…"
                    onChange={e => {
                      const name = e.target.value;
                      const match = materialByName[name];
                      updateLine(idx, { name, material_id: match ? match.id : '' });
                    }}
                  />
                  <input
                    type="number"
                    min="1"
                    className={`${inputCls} w-16 flex-none text-center`}
                    value={line.quantity}
                    onChange={e => updateLine(idx, { quantity: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() => removeLine(idx)}
                    disabled={lines.length === 1}
                    className="text-gray-400 hover:text-red-600 text-xl leading-none disabled:opacity-30 disabled:hover:text-gray-400"
                  >
                    &times;
                  </button>
                </div>
                {qty > 0 && (
                  <p className="text-xs text-gray-400 mt-0.5 pl-1">
                    × {siteIds.length} site{siteIds.length !== 1 ? 's' : ''} = {total} total
                  </p>
                )}
              </div>
            );
          })}
          <datalist id="material-request-options">
            {(materials || []).map(m => (
              <option key={m.id} value={m.name}>{m.unit ? `${m.name} (${m.unit})` : m.name}</option>
            ))}
          </datalist>
          <button type="button" onClick={addLine} className="text-sm text-blue-600 hover:underline">
            + Add material
          </button>
        </div>

        <div className="mb-5">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Notes <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <textarea
            className={`${inputCls} w-full`}
            rows={2}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Any additional context for the BOM Controller…"
          />
        </div>

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60"
          >
            {saving ? 'Sending…' : 'Send Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MaterialRequestResultModal({ result, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
        <div className="text-center">
          <div className="text-4xl mb-3">✅</div>
          <p className="text-gray-800 font-medium mb-1">Request sent</p>
          <p className="text-sm text-gray-500 mb-5">
            {result.site_count} site{result.site_count !== 1 ? 's' : ''} — {result.notified_count} BOM Controller{result.notified_count !== 1 ? 's' : ''} notified
          </p>
        </div>
        <div className="flex justify-center">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">Close</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pending Packing (Slice 3b) — pack + dispatch the itemized material-request
// flow (dispatch_line_items / batch_sites.packed_at from migration 015).
// Manual Part No. entry only; QR scanning is a later follow-up.
// ---------------------------------------------------------------------------

function packingSiteLabel(s) {
  const primary = s.govt_site_id || [s.taluk, s.gp].filter(Boolean).join(' / ') || s.site_id.slice(0, 8);
  return s.district_name ? `${primary} (${s.district_name})` : primary;
}

function PendingPacking({ role }) {
  const canPack = role === 'ADMIN' || role === 'BOM_CONTROLLER';
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    apiFetch(`${API}/dispatch-batches/pending`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => { setBatches(d); setSelected(new Set()); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Pending Packing</h1>
      </div>

      {loading && <Spinner />}
      {error && <ErrorMsg msg={error} />}
      {!loading && !error && batches.length === 0 && <EmptyState label="pending batches" />}
      {!loading && !error && batches.length > 0 && (
        <>
          {canPack && (
            <div
              className={`flex flex-wrap items-center gap-3 rounded-lg px-4 py-2.5 mb-3 border ${
                selected.size > 0 ? 'bg-blue-50 border-blue-200' : 'invisible border-transparent'
              }`}
            >
              <span className="text-sm font-medium text-blue-800">{selected.size} selected</span>
              <button onClick={() => setSelected(new Set())} className="text-xs text-blue-600 hover:underline">Clear</button>
              <div className="ml-auto">
                <button
                  onClick={() => setBulkDeleteOpen(true)}
                  className="px-3 py-1.5 text-sm font-medium bg-white border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                >
                  Delete Selected
                </button>
              </div>
            </div>
          )}
          <div className="bg-white rounded-xl shadow overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {canPack && (
                    <th className="px-4 py-3 text-left w-8">
                      <input
                        type="checkbox"
                        checked={batches.length > 0 && batches.every(b => selected.has(b.batch_id))}
                        onChange={e => {
                          setSelected(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) batches.forEach(b => next.add(b.batch_id));
                            else batches.forEach(b => next.delete(b.batch_id));
                            return next;
                          });
                        }}
                      />
                    </th>
                  )}
                  {['Batch', 'Created', 'Sites', 'Packed', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {batches.map(b => {
                  const total = b.sites.length;
                  const packed = b.sites.filter(s => s.packed_at).length;
                  return (
                    <tr
                      key={b.batch_id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedBatchId(b.batch_id)}
                    >
                      {canPack && (
                        <td className="px-4 py-3 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selected.has(b.batch_id)}
                            onChange={e => {
                              setSelected(prev => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(b.batch_id);
                                else next.delete(b.batch_id);
                                return next;
                              });
                            }}
                          />
                        </td>
                      )}
                      <td className="px-4 py-3 font-mono text-xs text-gray-600" title={b.batch_id}>{b.batch_code}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {b.created_at ? new Date(b.created_at).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-800">{total}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${
                          total > 0 && packed === total ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {packed} / {total} packed
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          className="text-xs text-blue-600 hover:underline"
                          onClick={e => { e.stopPropagation(); setSelectedBatchId(b.batch_id); }}
                        >Open</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {selectedBatchId && (
        <PackingBatchModal
          batchId={selectedBatchId}
          canPack={canPack}
          onClose={() => setSelectedBatchId(null)}
          onDispatched={() => { setSelectedBatchId(null); load(); }}
        />
      )}

      {bulkDeleteOpen && (
        <BulkDeleteBatchesModal
          batchIds={[...selected]}
          onClose={() => setBulkDeleteOpen(false)}
          onDone={() => { setBulkDeleteOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function BulkDeleteBatchesModal({ batchIds, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);

  const handleConfirm = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await apiFetch(`${API}/dispatch-batches/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batch_ids: batchIds }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      setResult(await r.json());
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => onDone(result)} />
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Batches Deleted</h2>
          <div className="mb-4 flex gap-4 text-sm">
            <span className="text-green-700 font-medium">✓ {result.deleted.length} deleted</span>
            {result.skipped.length > 0 && (
              <span className="text-amber-700 font-medium">⚠ {result.skipped.length} skipped</span>
            )}
          </div>
          {result.skipped.length > 0 && (
            <div className="mb-4 space-y-1.5 bg-amber-50 rounded-lg p-3 border border-amber-200 max-h-48 overflow-y-auto">
              {result.skipped.map(s => (
                <div key={s.batch_id} className="text-sm text-gray-700">
                  <span className="font-mono text-xs text-gray-500">{s.batch_id.slice(0, 8)}</span>: {s.reason}
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end">
            <button
              onClick={() => onDone(result)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
            >Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">
          Delete {batchIds.length} Batch{batchIds.length !== 1 ? 'es' : ''}?
        </h2>
        <p className="text-sm text-gray-600 mb-5">
          This permanently deletes the selected pending batches and resets material status for their sites.
          Any batch with a site already dispatched is skipped automatically and left untouched.
          This action cannot be undone.
        </p>
        {err && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleConfirm}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-60"
          >
            {busy ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PackingBatchModal({ batchId, canPack, onClose, onDispatched }) {
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [siteIndex, setSiteIndex] = useState(0);
  const [drafts, setDrafts] = useState({}); // line_item id -> in-progress Part No. text
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [dispatchResult, setDispatchResult] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiFetch(`${API}/dispatch-batches/pending`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      const found = d.find(b => b.batch_id === batchId) || null;
      setBatch(found);
      setLoading(false);
      return found;
    } catch (e) {
      setError(e.message);
      setLoading(false);
      return null;
    }
  };
  useEffect(() => { load(); }, [batchId]);

  // Seed the Part No. draft for any newly-seen line item, without clobbering
  // text the user is actively editing on a background reload.
  useEffect(() => {
    if (!batch) return;
    setDrafts(prev => {
      const next = { ...prev };
      let changed = false;
      batch.sites.forEach(s => s.line_items.forEach(li => {
        if (!(li.id in next)) { next[li.id] = li.part_no || ''; changed = true; }
      }));
      return changed ? next : prev;
    });
  }, [batch]);

  const sites = batch?.sites ?? [];
  useEffect(() => {
    setSiteIndex(i => Math.min(i, Math.max(0, sites.length - 1)));
  }, [sites.length]);

  const currentSite = sites[siteIndex];
  const packedCount = sites.filter(s => s.packed_at).length;

  const savePartNo = async lineItem => {
    const value = (drafts[lineItem.id] ?? '').trim();
    if (value === (lineItem.part_no || '')) return;
    setActionError(null);
    try {
      const r = await apiFetch(`${API}/dispatch-line-items/${lineItem.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ part_no: value || null }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const updated = await r.json();
      setBatch(b => !b ? b : {
        ...b,
        sites: b.sites.map(s => ({
          ...s,
          line_items: s.line_items.map(li => li.id === updated.id ? { ...li, part_no: updated.part_no } : li),
        })),
      });
    } catch (e) {
      setActionError(e.message);
    }
  };

  const markPacked = async () => {
    if (!currentSite || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      const r = await apiFetch(`${API}/dispatch-batches/${batchId}/sites/${currentSite.site_id}/pack`, {
        method: 'POST',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const fresh = await load();
      if (fresh) {
        const forward = fresh.sites.findIndex((s, i) => i > siteIndex && !s.packed_at);
        const anyUnpacked = forward !== -1 ? forward : fresh.sites.findIndex(s => !s.packed_at);
        if (anyUnpacked !== -1) setSiteIndex(anyUnpacked);
      }
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const dispatchNow = async () => {
    if (busy) return;
    setBusy(true);
    setActionError(null);
    try {
      const r = await apiFetch(`${API}/dispatch-batches/${batchId}/dispatch`, { method: 'POST' });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      setDispatchResult(await r.json());
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-8">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-xl mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800" title={batchId}>
            Batch {batch ? batch.batch_code : batchId.slice(0, 8)}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        {loading && <Spinner />}
        {error && <ErrorMsg msg={error} />}

        {!loading && !error && !batch && (
          <div className="text-center py-10 text-gray-400">
            <div className="text-4xl mb-3">✅</div>
            <p className="text-sm">This batch is no longer pending — every site has been dispatched.</p>
            <button onClick={onClose} className="mt-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">Close</button>
          </div>
        )}

        {!loading && !error && batch && dispatchResult && (
          <div className="text-center py-6">
            <div className="text-4xl mb-3">🚚</div>
            <p className="text-gray-800 font-medium mb-1">Dispatch complete</p>
            <p className="text-sm text-gray-500 mb-5">
              {dispatchResult.dispatched_count} site{dispatchResult.dispatched_count !== 1 ? 's' : ''} dispatched
              {dispatchResult.pending_count > 0 ? `, ${dispatchResult.pending_count} still pending` : ''}
            </p>
            <button onClick={onDispatched} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">
              Back to Pending Packing
            </button>
          </div>
        )}

        {!loading && !error && batch && !dispatchResult && (
          <div>
            {actionError && <div className="mb-4"><ErrorMsg msg={actionError} /></div>}

            {/* Pack Summary — aggregated per-material totals across all
                not-yet-dispatched sites in this batch, for reference while
                pulling stock. Read-only. */}
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Pack Summary</p>
              {batch.material_totals.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No itemized materials on this batch.</p>
              ) : (
                <ul className="text-sm text-gray-700 space-y-1">
                  {batch.material_totals.map(t => (
                    <li key={t.material_id} className="flex justify-between">
                      <span>{t.material_name}</span>
                      <span className="font-medium">{t.total_quantity}{t.unit ? ` ${t.unit}` : ''}</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-xs text-gray-400 mt-2">{packedCount} of {sites.length} sites packed</p>
            </div>

            {/* Per-site packing — same prev/next stepping pattern as
                SurveyDetailModal. */}
            {currentSite && (
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-start gap-2 mb-4">
                  <button
                    onClick={() => setSiteIndex(i => Math.max(0, i - 1))}
                    disabled={siteIndex === 0}
                    className="mt-0.5 text-2xl leading-none text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:text-gray-400 shrink-0"
                  >‹</button>
                  <div className="flex-1 min-w-0 text-center">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {packingSiteLabel(currentSite)}
                      {currentSite.packed_at && <span className="ml-1.5 text-green-600" title="Packed">✅</span>}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">Site {siteIndex + 1} of {sites.length}</p>
                  </div>
                  <button
                    onClick={() => setSiteIndex(i => Math.min(sites.length - 1, i + 1))}
                    disabled={siteIndex === sites.length - 1}
                    className="mt-0.5 text-2xl leading-none text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:text-gray-400 shrink-0"
                  >›</button>
                </div>

                {currentSite.line_items.length === 0 ? (
                  <p className="text-sm text-gray-400 italic mb-4">No itemized materials for this site.</p>
                ) : (
                  <div className="space-y-2 mb-4">
                    {currentSite.line_items.map(li => (
                      <div key={li.id} className="flex items-center gap-3 bg-white rounded-lg border border-gray-200 px-3 py-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-800 truncate">{li.material_name}</p>
                          <p className="text-xs text-gray-500">{li.quantity}{li.unit ? ` ${li.unit}` : ''}</p>
                        </div>
                        <input
                          className="w-32 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                          placeholder="Part No."
                          disabled={!canPack}
                          value={drafts[li.id] ?? ''}
                          onChange={e => setDrafts(d => ({ ...d, [li.id]: e.target.value }))}
                          onBlur={() => savePartNo(li)}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {canPack && (
                  <button
                    onClick={markPacked}
                    disabled={busy}
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-60"
                  >
                    {currentSite.packed_at ? 'Re-mark Packed' : 'Mark Packed'}
                  </button>
                )}
              </div>
            )}

            {canPack && packedCount > 0 && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={dispatchNow}
                  disabled={busy}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60"
                >
                  {busy ? 'Dispatching…' : `Dispatch Now (${packedCount} packed)`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ImportFaultsModal({ onClose, onDone }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const handleSubmit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    setErr(null);
    try {
      const r = await apiFetch(`${API}/sites/bulk/fault-import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_ids_text: text }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      onDone(await r.json());
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Import Faults</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <p className="text-sm text-gray-500 mb-3">
          Paste government site IDs — newline, comma, space, or tab separated.
        </p>
        {err && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}
        <textarea
          className="w-full h-48 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={'1030\n1032\n1039, 1041 ...'}
        />
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={!text.trim() || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60"
          >
            {saving ? 'Importing…' : 'Import'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ImportFaultsResultModal({ result, onClose }) {
  const [copied, setCopied] = useState(null);

  const copy = (label, list) => {
    navigator.clipboard?.writeText(list.join('\n'));
    setCopied(label);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Import Faults — Result</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <p className="text-sm text-gray-600 mb-4">{result.matched} matched and marked FAULT.</p>
        {result.updated.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Updated ({result.updated.length})</p>
              <button onClick={() => copy('updated', result.updated)} className="text-xs text-blue-600 hover:underline">
                {copied === 'updated' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <textarea
              readOnly
              className="w-full h-24 text-xs font-mono border border-green-200 bg-green-50 rounded-lg p-2"
              value={result.updated.join('\n')}
            />
          </div>
        )}
        {result.unmatched_ids.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Unmatched ({result.unmatched_ids.length})</p>
              <button onClick={() => copy('unmatched', result.unmatched_ids)} className="text-xs text-blue-600 hover:underline">
                {copied === 'unmatched' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <textarea
              readOnly
              className="w-full h-24 text-xs font-mono border border-amber-200 bg-amber-50 rounded-lg p-2"
              value={result.unmatched_ids.join('\n')}
            />
          </div>
        )}
        <div className="flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">Close</button>
        </div>
      </div>
    </div>
  );
}

function SiteMasterDetailsModal({ site, onClose, onSaved }) {
  const [helperName, setHelperName] = useState(site.helper_name ?? '');
  const [helperContact, setHelperContact] = useState(site.helper_contact ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const handleSubmit = async () => {
    setSaving(true);
    setErr(null);
    try {
      const body = {
        helper_name: helperName === '' ? null : helperName,
        helper_contact: helperContact === '' ? null : helperContact,
      };
      const r = await apiFetch(`${API}/sites/${site.id}/master-details`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      onSaved();
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Edit Site Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          {site.govt_site_id || site.site_code}{site.district_name ? ` — ${site.district_name}` : ''}
        </p>
        {err && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-600 mb-1">GPS (read-only)</label>
          <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            {site.govt_lat != null && site.govt_lon != null
              ? `${Number(site.govt_lat).toFixed(7)}, ${Number(site.govt_lon).toFixed(7)}`
              : 'Not set'}
          </p>
          <p className="text-xs text-gray-400 mt-1">Use Bulk Correct GPS or the Installation Reports edit tool to change this.</p>
        </div>
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-600 mb-1">Helper Name</label>
          <input className={inputCls} type="text" value={helperName} onChange={e => setHelperName(e.target.value)} />
        </div>
        <div className="mb-5">
          <label className="block text-xs font-medium text-gray-600 mb-1">Helper Contact</label>
          <input className={inputCls} type="text" value={helperContact} onChange={e => setHelperContact(e.target.value)} />
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkSiteDetailsCsvModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const handleSubmit = async () => {
    if (!file) return;
    setSaving(true);
    setErr(null);
    try {
      const csvText = await file.text();
      const r = await apiFetch(`${API}/sites/bulk/site-details-csv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv_text: csvText }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      onDone(await r.json());
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  const codeCls = 'font-mono text-xs bg-gray-100 px-1 rounded';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Bulk Update Site Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <p className="text-sm text-gray-500 mb-3">
          Upload a .csv file with columns <span className={codeCls}>govt_site_id</span> (required — matches the site),
          and either or both of <span className={codeCls}>helper_name</span>, <span className={codeCls}>helper_contact</span>{' '}
          (optional — blank cells are left unchanged). GPS is not editable via this tool — use Bulk Correct GPS instead.
        </p>
        {err && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={e => setFile(e.target.files?.[0] || null)}
          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 mb-5 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-blue-50 file:text-blue-700 file:text-sm file:font-medium hover:file:bg-blue-100"
        />
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={!file || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60"
          >
            {saving ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkSiteDetailsCsvResultModal({ result, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Bulk Update Site Details — Result</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="mb-4 flex gap-4 text-sm flex-wrap">
          <span className="text-green-700 font-medium">✓ {result.updated.length} updated</span>
          <span className="text-amber-700 font-medium">⚠ {result.skipped_not_found.length} not found</span>
          <span className="text-red-700 font-medium">✗ {result.skipped_invalid.length} invalid</span>
        </div>
        {result.updated.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Updated</p>
            <div className="space-y-1.5 bg-green-50 rounded-lg p-3 border border-green-200">
              {result.updated.map((u, i) => (
                <div key={`${u.govt_site_id}-${i}`} className="text-sm text-gray-700">
                  <span className="font-mono text-xs text-gray-500">{u.govt_site_id}</span>: {u.fields_updated.join(', ')}
                </div>
              ))}
            </div>
          </div>
        )}
        {result.skipped_not_found.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Not Found</p>
            <div className="space-y-1.5 bg-amber-50 rounded-lg p-3 border border-amber-200">
              {result.skipped_not_found.map((s, i) => (
                <div key={`${s.govt_site_id}-${i}`} className="text-sm text-gray-700">
                  <span className="font-mono text-xs text-gray-500">{s.govt_site_id}</span>: {s.reason}
                </div>
              ))}
            </div>
          </div>
        )}
        {result.skipped_invalid.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Invalid</p>
            <div className="space-y-1.5 bg-red-50 rounded-lg p-3 border border-red-200">
              {result.skipped_invalid.map((s, i) => (
                <div key={`${s.govt_site_id}-${i}`} className="text-sm text-gray-700">
                  <span className="font-mono text-xs text-gray-500">{s.govt_site_id || '(missing id)'}</span>: {s.reason}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">Close</button>
        </div>
      </div>
    </div>
  );
}

const PAGE_SIZE = 20;

function Sites({ role }) {
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [sitesError, setSitesError] = useState(null);

  const { data: districts } = useFetch(`${API}/districts`);
  const { data: projects } = useFetch(`${API}/projects`);
  const { data: personnel } = useFetch(`${API}/personnel`);
  const { data: installVisits } = useFetch(`${API}/installation-visits`);

  const [filterZone, setFilterZone] = useState('');
  const [filterDistrict, setFilterDistrict] = useState('');
  const [filterTaluk, setFilterTaluk] = useState('');
  const [filterSiteStatus, setFilterSiteStatus] = useState('');
  const [filterJobType, setFilterJobType] = useState('');
  const [filterMtlStatus, setFilterMtlStatus] = useState('');
  const [filterStaff, setFilterStaff] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [modal, setModal] = useState(null);
  const [editSite, setEditSite] = useState(null);
  const [editMasterSite, setEditMasterSite] = useState(null);
  const [deleteSite, setDeleteSite] = useState(null);
  const [assignSite, setAssignSite] = useState(null);
  const [actionSite, setActionSite] = useState(null);
  const [overrideSite, setOverrideSite] = useState(null);
  const [surveyIdx, setSurveyIdx] = useState(null);
  const [installDetailIdx, setInstallDetailIdx] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [changedIds, setChangedIds] = useState(new Set());
  const [spinning, setSpinning] = useState(false);
  const sitesRef = useRef([]);

  // Bulk operations (Phase B)
  const [selected, setSelected] = useState(new Set());
  const [bulkModal, setBulkModal] = useState(null); // 'status' | 'assign' | 'materials'
  const [assignResult, setAssignResult] = useState(null);
  const [materialRequestResult, setMaterialRequestResult] = useState(null);

  const staffById = useMemo(() => {
    if (!personnel) return {};
    return Object.fromEntries(personnel.map(p => [p.id, p]));
  }, [personnel]);

  const sitesById = useMemo(
    () => Object.fromEntries(sites.map(s => [s.id, s])),
    [sites]
  );

  useEffect(() => { sitesRef.current = sites; }, [sites]);

  const loadSites = () => {
    setSitesLoading(true);
    setSitesError(null);
    apiFetch(`${API}/sites`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => {
        setSites(d);
        setSitesLoading(false);
        setLastUpdated(new Date());
      })
      .catch(e => { setSitesError(e.message); setSitesLoading(false); });
  };

  const handleManualRefresh = () => {
    if (spinning) return;
    setSpinning(true);
    apiFetch(`${API}/sites`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(fresh => {
        setSpinning(false);
        const current = sitesRef.current;
        const byId = Object.fromEntries(current.map(s => [s.id, s]));

        const changed = new Set(
          fresh
            .filter(s => {
              const prev = byId[s.id];
              return !prev
                || prev.site_status !== s.site_status
                || prev.mtl_status !== s.mtl_status
                || prev.current_job_id !== s.current_job_id
                || prev.assigned_staff_id !== s.assigned_staff_id;
            })
            .map(s => s.id)
        );

        setLastUpdated(new Date());

        if (changed.size === 0 && fresh.length === current.length) return;

        setSites(fresh);
        if (changed.size > 0) {
          setChangedIds(changed);
          setTimeout(() => setChangedIds(new Set()), 2000);
        }
      })
      .catch(() => setSpinning(false));
  };

  useEffect(() => { loadSites(); }, []);

  const zones = useMemo(() =>
    [...new Set(sites.map(s => s.division).filter(Boolean))].sort(),
    [sites]);

  const districtOptions = useMemo(() => {
    if (!districts) return [];
    return filterZone ? districts.filter(d => d.division === filterZone) : districts;
  }, [districts, filterZone]);

  const talukOptions = useMemo(() => {
    let s = sites;
    if (filterZone) s = s.filter(x => x.division === filterZone);
    if (filterDistrict) s = s.filter(x => x.district_id === filterDistrict);
    return [...new Set(s.map(x => x.taluk).filter(Boolean))].sort();
  }, [sites, filterZone, filterDistrict]);

  const staffOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    sites.forEach(s => {
      if (s.assigned_staff_id && !seen.has(s.assigned_staff_id)) {
        seen.add(s.assigned_staff_id);
        const p = staffById[s.assigned_staff_id];
        opts.push({ id: s.assigned_staff_id, name: p?.full_name || p?.name || s.assigned_staff_id.slice(0, 8) });
      }
    });
    return opts.sort((a, b) => a.name.localeCompare(b.name));
  }, [sites, staffById]);

  const filtered = useMemo(() => {
    let s = sites;
    if (filterZone) s = s.filter(x => x.division === filterZone);
    if (filterDistrict) s = s.filter(x => x.district_id === filterDistrict);
    if (filterTaluk) s = s.filter(x => x.taluk === filterTaluk);
    if (filterSiteStatus) s = s.filter(x => x.site_status === filterSiteStatus);
    if (filterJobType) s = s.filter(x => x.current_job?.job_type === filterJobType);
    if (filterMtlStatus) s = s.filter(x => x.mtl_status === filterMtlStatus);
    if (filterStaff === '__unassigned__') s = s.filter(x => !x.assigned_staff_id);
    else if (filterStaff) s = s.filter(x => x.assigned_staff_id === filterStaff);
    if (search) {
      const q = search.toLowerCase();
      s = s.filter(x =>
        x.site_code.toLowerCase().includes(q) || (x.gp || '').toLowerCase().includes(q)
      );
    }
    return s;
  }, [sites, filterZone, filterDistrict, filterTaluk, filterSiteStatus, filterJobType, filterMtlStatus, filterStaff, search]);

  // Survey/site-detail icon: still keyed off install_status, since the plain
  // initial-survey step (GOVT_PROVIDED → SURVEYED) isn't modeled as a job or
  // a distinct site_status value — install_status remains the only signal
  // for "has a survey been submitted for this site".
  const HAS_SURVEY = new Set([
    'SURVEYED', 'APPROVED',
    'SENDING_CIVIL_MTLS', 'CIVIL_MTLS_DISPATCHED', 'CIVIL_MTLS_RECEIVED',
    'CIVIL_PENDING', 'CIVIL_DONE',
    'SENDING_UNITS', 'UNITS_DISPATCHED', 'UNITS_RECEIVED',
    'INSTLN_PENDING', 'INSTLN_COMPLETE', 'READY_FOR_INSTALL',
    'INSTALLED', 'COMMISSIONED',
  ]);
  const surveyedSites = useMemo(
    () => filtered.filter(s => HAS_SURVEY.has(s.install_status)),
    [filtered]
  );

  useEffect(() => setPage(1), [filterZone, filterDistrict, filterTaluk, filterSiteStatus, filterJobType, filterMtlStatus, filterStaff, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleDelete = async () => {
    await apiFetch(`${API}/sites/${deleteSite.id}`, { method: 'DELETE' });
    setDeleteSite(null);
    loadSites();
  };

  const downloadCSV = () => {
    const headers = [
      'site_id', 'division', 'district_name', 'taluk', 'gp',
      'site_status', 'job', 'materials', 'location_label',
    ];
    const escape = v => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = filtered.map(s => {
      const loc = [s.district_name, s.taluk, s.gp].filter(Boolean).join(' / ');
      const job = s.current_job
        ? `${s.current_job.job_type.toLowerCase()} — ${s.current_job.assigned_staff_name || ''}`
        : '';
      return [
        s.govt_site_id, s.division, s.district_name, s.taluk, s.gp,
        s.site_status, job, s.mtl_status, loc,
      ].map(escape).join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');

    let filename = 'all_sites.csv';
    if (filterTaluk) {
      const distName = districts?.find(d => d.id === filterDistrict)?.name || filterDistrict;
      filename = `${distName}_${filterTaluk}_sites.csv`.replace(/\s+/g, '_');
    } else if (filterDistrict) {
      const distName = districts?.find(d => d.id === filterDistrict)?.name || filterDistrict;
      filename = `${distName}_sites.csv`.replace(/\s+/g, '_');
    } else if (filterZone) {
      filename = `${filterZone}_sites.csv`.replace(/\s+/g, '_');
    }

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  console.log('Sites role prop:', role);
  const canAddSite    = role !== 'VIEWER';
  const canDeleteSite = role === 'ADMIN';
  const canEditSiteMaster = role === 'ADMIN';
  const canAssign     = role !== 'VIEWER';
  const canOverride   = role !== 'VIEWER';
  const canBulk       = role === 'ADMIN' || role === 'SUPERVISOR'; // BOM_CONTROLLER is non-VIEWER but excluded here

  const selectCls = 'border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500';

  const thFilterCls = 'border border-gray-200 rounded px-2 py-1 text-xs w-full font-normal normal-case tracking-normal bg-white focus:outline-none focus:ring-1 focus:ring-blue-400';

  return (
    <div>
      {/* ── Sticky bar 1: title + count + refresh (scrolls with main, sticks at top) ── */}
      <div className="sticky top-0 z-20 bg-gray-100 -mx-6 px-6 -mt-3 py-3 flex items-center justify-between border-b border-gray-200">
        <div className="flex items-baseline gap-3">
          <h1 className="text-2xl font-bold text-gray-800">Sites</h1>
          <span className="text-sm text-gray-400">{filtered.length} site{filtered.length !== 1 ? 's' : ''}</span>
        </div>
        <button
          onClick={handleManualRefresh}
          disabled={spinning}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-40"
        >
          <span className={spinning ? 'animate-spin inline-block' : 'inline-block'}>↻</span>
          Refresh
        </button>
      </div>

      {/* ── Scrollable zone: Division, District, CSV, Add Site ── */}
      <div className="flex flex-wrap items-end gap-3 py-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Division</label>
          <select
            className={selectCls}
            value={filterZone}
            onChange={e => { setFilterZone(e.target.value); setFilterDistrict(''); setFilterTaluk(''); }}
          >
            <option value="">All Divisions</option>
            {zones.map(z => <option key={z}>{z}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">District</label>
          <select
            className={selectCls}
            value={filterDistrict}
            onChange={e => { setFilterDistrict(e.target.value); setFilterTaluk(''); }}
          >
            <option value="">All Districts</option>
            {districtOptions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {lastUpdated && (
            <span className="text-xs text-gray-400 hidden sm:block">
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={downloadCSV}
            disabled={sitesLoading || filtered.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-40"
          >
            ↓ CSV
          </button>
          {canAddSite && (
            <button
              onClick={() => setModal('add')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
            >
              + Add Site
            </button>
          )}
        </div>
      </div>

      {canBulk && (
        <div
          className={`flex flex-wrap items-center gap-3 rounded-lg px-4 py-2.5 mb-3 border ${
            selected.size > 0
              ? 'bg-blue-50 border-blue-200'
              : 'invisible border-transparent'
          }`}
        >
          <span className="text-sm font-medium text-blue-800">{selected.size} selected</span>
          <button onClick={() => setSelected(new Set())} className="text-xs text-blue-600 hover:underline">
            Clear
          </button>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <button
              onClick={() => setBulkModal('status')}
              className="px-3 py-1.5 text-sm font-medium bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Set Status
            </button>
            <button
              onClick={() => setBulkModal('assign')}
              className="px-3 py-1.5 text-sm font-medium bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Assign Jobs
            </button>
            <button
              onClick={() => setBulkModal('materials')}
              className="px-3 py-1.5 text-sm font-medium bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Request Materials
            </button>
          </div>
        </div>
      )}

      {sitesLoading && <Spinner />}
      {sitesError && <ErrorMsg msg={sitesError} />}
      {!sitesLoading && !sitesError && (
        <>
          {/* ── Table: thead is sticky bar 2 (top-14 = below sticky bar 1) ──
               No overflow-x-auto here: thead's sticky position must resolve
               against the page scroll container (same as sticky bar 1
               above), not a nested one — an overflow wrapper here would
               anchor the sticky offset to this box instead of the viewport,
               pushing the header (and hiding rows) whenever the box is
               shorter than the header's own stuck offset. ── */}
          <div className="bg-white rounded-xl shadow">
            <table className="min-w-full text-sm">
                  <thead className="sticky top-14 z-10 bg-gray-50 border-b">
                    <tr>
                      {canBulk && (
                        <th className="px-3 pt-2 pb-3 text-left align-top w-8">
                          <input
                            type="checkbox"
                            checked={paginated.length > 0 && paginated.every(s => selected.has(s.id))}
                            onChange={e => {
                              setSelected(prev => {
                                const next = new Set(prev);
                                if (e.target.checked) paginated.forEach(s => next.add(s.id));
                                else paginated.forEach(s => next.delete(s.id));
                                return next;
                              });
                            }}
                          />
                          {paginated.length > 0
                            && paginated.every(s => selected.has(s.id))
                            && filtered.length > paginated.length && (
                            <button
                              className="block text-xs text-blue-600 hover:underline mt-1 whitespace-nowrap"
                              onClick={() => setSelected(new Set(filtered.map(s => s.id)))}
                            >
                              Select all {filtered.length}
                            </button>
                          )}
                        </th>
                      )}
                      <th className="px-3 pt-2 pb-1 text-left align-top w-36">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Site ID</div>
                        <input
                          type="text"
                          className={thFilterCls}
                          value={search}
                          onChange={e => setSearch(e.target.value)}
                          placeholder="ID or GP…"
                        />
                      </th>
                      <th className="px-3 pt-2 pb-1 text-left align-top">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Taluk</div>
                        <select
                          className={thFilterCls}
                          value={filterTaluk}
                          onChange={e => setFilterTaluk(e.target.value)}
                        >
                          <option value="">All</option>
                          {talukOptions.map(t => <option key={t}>{t}</option>)}
                        </select>
                      </th>
                      <th className="px-3 pt-2 pb-3 text-left align-top">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">GP</div>
                      </th>
                      <th className="px-3 pt-2 pb-1 text-left align-top w-32">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Site Status</div>
                        <select
                          className={thFilterCls}
                          value={filterSiteStatus}
                          onChange={e => setFilterSiteStatus(e.target.value)}
                        >
                          <option value="">— All —</option>
                          {SITE_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                        </select>
                      </th>
                      <th className="px-3 pt-2 pb-1 text-left align-top w-28">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Job</div>
                        <select
                          className={thFilterCls}
                          value={filterJobType}
                          onChange={e => setFilterJobType(e.target.value)}
                        >
                          <option value="">— All —</option>
                          {JOB_TYPES.map(t => <option key={t} value={t}>{t.toLowerCase()}</option>)}
                        </select>
                      </th>
                      <th className="px-3 pt-2 pb-1 text-left align-top w-28">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Materials</div>
                        <select
                          className={thFilterCls}
                          value={filterMtlStatus}
                          onChange={e => setFilterMtlStatus(e.target.value)}
                        >
                          <option value="">— All —</option>
                          {MTL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </th>
                      <th className="px-3 pt-2 pb-1 text-left align-top w-32">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Receipt</div>
                      </th>
                      <th className="px-3 pt-2 pb-1 text-left align-top">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Assigned To</div>
                        <select
                          className={thFilterCls}
                          value={filterStaff}
                          onChange={e => setFilterStaff(e.target.value)}
                        >
                          <option value="">All</option>
                          <option value="__unassigned__">Unassigned</option>
                          {staffOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                        </select>
                      </th>
                      <th className="px-3 pt-2 pb-3 text-left align-top w-8" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={canBulk ? 10 : 9} className="px-4 py-10 text-center text-sm text-gray-400">
                          No sites found matching the selected filters.
                        </td>
                      </tr>
                    ) : paginated.map(s => {
                      const openEdit = () => { setEditSite(s); setModal('edit'); };
                      return (
                        <tr
                          key={s.id}
                          className={`transition-colors duration-1000 ${changedIds.has(s.id) ? 'bg-yellow-100' : 'hover:bg-blue-50'}`}
                        >
                          {canBulk && (
                            <td className="px-3 py-3 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selected.has(s.id)}
                                onClick={e => e.stopPropagation()}
                                onChange={e => {
                                  setSelected(prev => {
                                    const next = new Set(prev);
                                    if (e.target.checked) next.add(s.id);
                                    else next.delete(s.id);
                                    return next;
                                  });
                                }}
                              />
                            </td>
                          )}
                          <td className="px-3 py-3 whitespace-nowrap">
                            {s.govt_site_id ? (
                              <button className="text-left" onClick={openEdit}>
                                <span className="font-mono text-xs font-medium text-blue-600 hover:underline block">{s.govt_site_id}</span>
                                {s.district_name && <span className="text-xs text-gray-400 block mt-0.5">{s.district_name}</span>}
                              </button>
                            ) : (
                              <button className="text-left italic text-gray-400 hover:text-blue-600 text-xs" onClick={openEdit}>
                                {s.district_name || '—'}
                              </button>
                            )}
                          </td>
                          <td className="px-3 py-3 text-gray-600">{s.taluk || '—'}</td>
                          <td className="px-3 py-3 text-gray-600">{s.gp || '—'}</td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            {s.site_status !== 'NO_ACTION' && (
                              canOverride ? (
                                <button
                                  title="Click to override site status"
                                  onClick={e => { e.stopPropagation(); setOverrideSite({ site: s, field: 'site_status' }); }}
                                  className="hover:opacity-75 hover:ring-2 hover:ring-blue-300 rounded transition-all"
                                >
                                  <SiteStatusBadge status={s.site_status} />
                                </button>
                              ) : (
                                <SiteStatusBadge status={s.site_status} />
                              )
                            )}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-700">
                            {s.current_job && (
                              <span>{s.current_job.job_type.toLowerCase()}</span>
                            )}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            {s.mtl_status !== 'NA' && (
                              canOverride ? (
                                <button
                                  title="Click to override materials status"
                                  onClick={e => { e.stopPropagation(); setOverrideSite({ site: s, field: 'mtl_status' }); }}
                                  className="hover:opacity-75 hover:ring-2 hover:ring-blue-300 rounded transition-all"
                                >
                                  <MtlStatusBadge status={s.mtl_status} />
                                </button>
                              ) : (
                                <MtlStatusBadge status={s.mtl_status} />
                              )
                            )}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            {s.receipt_info && (
                              <div>
                                <ReceiptStatusBadge info={s.receipt_info} />
                                {s.receipt_info.recipient_name && (
                                  <div className="text-[11px] text-gray-400 mt-0.5">{s.receipt_info.recipient_name}</div>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-3 text-gray-600 whitespace-nowrap">
                            {s.assigned_staff_id
                              ? (staffById[s.assigned_staff_id]?.full_name || staffById[s.assigned_staff_id]?.name || <span className="text-xs text-gray-400 font-mono">{s.assigned_staff_id.slice(0, 8)}…</span>)
                              : <span className="text-xs text-gray-400 italic">Unassigned</span>
                            }
                          </td>
                          <td className="px-3 py-3 text-right whitespace-nowrap">
                            {(s.install_status === 'INSTALLED' || s.install_status === 'COMMISSIONED') && installVisits && (() => {
                              const idx = installVisits.findIndex(v => v.site_id === s.id);
                              if (idx === -1) return null;
                              return (
                                <button
                                  title="View installation report"
                                  className="mr-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 hover:ring-2 hover:ring-green-300 transition-all"
                                  onClick={e => { e.stopPropagation(); setInstallDetailIdx(idx); }}
                                >
                                  INSTALLED
                                </button>
                              );
                            })()}
                            {HAS_SURVEY.has(s.install_status) && (
                              <button
                                title="View survey detail"
                                className="mr-2 text-base leading-none hover:scale-110 transition-transform"
                                onClick={e => { e.stopPropagation(); setSurveyIdx(surveyedSites.findIndex(x => x.id === s.id)); }}
                              >
                                📋
                              </button>
                            )}
                            {canAssign && (
                              <button
                                title="Assign staff"
                                className="mr-2 text-xs text-indigo-500 hover:text-indigo-700 hover:underline"
                                onClick={e => { e.stopPropagation(); setAssignSite(s); }}
                              >
                                👤
                              </button>
                            )}
                            {role !== 'VIEWER' && s.mtl_status === 'REQUESTED' && (
                              <button
                                title="Confirm dispatch"
                                className="mr-2 text-xs font-medium px-1.5 py-0.5 rounded bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"
                                onClick={e => { e.stopPropagation(); setActionSite({ site: s, actionDef: { label: '✓ Dispatched', action: 'CONFIRM_DISPATCH' } }); }}
                              >
                                ✓ Dispatched
                              </button>
                            )}
                            {canEditSiteMaster && (
                              <button
                                title="Edit GPS & helper contact"
                                className="mr-2 text-xs text-indigo-500 hover:text-indigo-700 hover:underline"
                                onClick={e => { e.stopPropagation(); setEditMasterSite(s); }}
                              >
                                Edit Details
                              </button>
                            )}
                            {canDeleteSite && (
                              <button
                                className="text-xs text-red-500 hover:text-red-700 hover:underline"
                                onClick={e => { e.stopPropagation(); setDeleteSite(s); }}
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
          </div>
          {filtered.length > 0 && (
          <div className="flex justify-between items-center mt-4 text-sm text-gray-600">
                <span>
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(p => p - 1)}
                    disabled={page === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                  >
                    ‹ Prev
                  </button>
                  <span className="px-2 font-medium">Page {page} / {totalPages}</span>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                  >
                    Next ›
                  </button>
                  <input
                    type="number"
                    min={1}
                    max={totalPages}
                    placeholder="Go to…"
                    className="w-20 px-2 py-1 border border-gray-300 rounded-lg text-sm text-center"
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        const n = parseInt(e.target.value, 10);
                        if (n >= 1 && n <= totalPages) setPage(n);
                        e.target.value = '';
                      }
                    }}
                  />
                </div>
          </div>
          )}
        </>
      )}

      {(modal === 'add' || modal === 'edit') && (
        <SiteFormModal
          mode={modal}
          site={modal === 'edit' ? editSite : null}
          projects={projects}
          siteZones={zones}
          onClose={() => { setModal(null); setEditSite(null); }}
          onSaved={() => { setModal(null); setEditSite(null); loadSites(); }}
        />
      )}

      {deleteSite && (
        <DeleteConfirmModal
          site={deleteSite}
          onConfirm={handleDelete}
          onCancel={() => setDeleteSite(null)}
        />
      )}

      {assignSite && (
        <AssignModal
          site={assignSite}
          personnel={personnel}
          onClose={() => setAssignSite(null)}
          onAssigned={() => { setAssignSite(null); loadSites(); }}
        />
      )}

      {actionSite && (
        <ActionModal
          site={actionSite.site}
          actionDef={actionSite.actionDef}
          onClose={() => setActionSite(null)}
          onDone={() => { setActionSite(null); loadSites(); }}
        />
      )}

      {overrideSite && (
        <OverrideTrackModal
          site={overrideSite.site}
          initialField={overrideSite.field}
          onClose={() => setOverrideSite(null)}
          onUpdated={() => { setOverrideSite(null); loadSites(); }}
        />
      )}

      {surveyIdx !== null && surveyedSites.length > 0 && (
        <SurveyDetailModal
          site={surveyedSites[surveyIdx]}
          onClose={() => setSurveyIdx(null)}
          surveyedSites={surveyedSites}
          surveyIndex={surveyIdx}
          onNavigate={setSurveyIdx}
        />
      )}

      {installDetailIdx !== null && installVisits && installVisits.length > 0 && (
        <InstallationDetailModal
          visits={installVisits}
          index={installDetailIdx}
          onClose={() => setInstallDetailIdx(null)}
          onNavigate={setInstallDetailIdx}
          isAdmin={role === 'ADMIN'}
          onUpdated={loadSites}
        />
      )}

      {bulkModal === 'status' && (
        <BulkStatusModal
          siteIds={[...selected]}
          onClose={() => setBulkModal(null)}
          onDone={() => { setBulkModal(null); setSelected(new Set()); loadSites(); }}
        />
      )}

      {bulkModal === 'assign' && (
        <BulkAssignJobsModal
          siteIds={[...selected]}
          personnel={personnel}
          onClose={() => setBulkModal(null)}
          onDone={result => { setBulkModal(null); setSelected(new Set()); setAssignResult(result); loadSites(); }}
        />
      )}

      {assignResult && (
        <BulkAssignResultModal
          result={assignResult}
          sitesById={sitesById}
          onClose={() => setAssignResult(null)}
        />
      )}

      {bulkModal === 'materials' && (
        <MaterialRequestModal
          siteIds={[...selected]}
          sitesById={sitesById}
          onClose={() => setBulkModal(null)}
          onDone={result => { setBulkModal(null); setSelected(new Set()); setMaterialRequestResult(result); loadSites(); }}
        />
      )}

      {materialRequestResult && (
        <MaterialRequestResultModal
          result={materialRequestResult}
          onClose={() => setMaterialRequestResult(null)}
        />
      )}

      {editMasterSite && (
        <SiteMasterDetailsModal
          site={editMasterSite}
          onClose={() => setEditMasterSite(null)}
          onSaved={() => { setEditMasterSite(null); loadSites(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fault Tasks page
// ---------------------------------------------------------------------------
function FaultStatusBadge({ status }) {
  const colors = {
    OPEN: 'bg-green-100 text-green-800',
    IN_PROGRESS: 'bg-blue-100 text-blue-800',
    RESOLVED: 'bg-gray-100 text-gray-700',
    DUPLICATE: 'bg-yellow-100 text-yellow-800',
    MERGED: 'bg-purple-100 text-purple-800',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
}

function FaultTasks() {
  const { data, loading, error } = useFetch(`${API}/fault-tasks`);

  function rowClass(task) {
    const days = task.days_elapsed ?? 0;
    if (days >= 5) return 'bg-red-50 border-l-4 border-red-500';
    if (days >= 3) return 'bg-yellow-50 border-l-4 border-yellow-400';
    return 'border-l-4 border-green-400';
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-2">Fault Tasks</h1>
      <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-600">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 bg-green-400 rounded-sm inline-block" />
          Open / &lt;3 days
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 bg-yellow-400 rounded-sm inline-block" />
          Day 3+
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 bg-red-500 rounded-sm inline-block" />
          Day 5+ (SLA breach risk)
        </span>
      </div>
      {loading && <Spinner />}
      {error && <ErrorMsg msg={error} />}
      {!loading && !error && (
        data?.length === 0
          ? <EmptyState label="fault tasks" />
          : <div className="bg-white rounded-xl shadow overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {['Site ID', 'Fault Type', 'Reported', 'SLA Due', 'Days Elapsed', 'Status'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.map(t => (
                    <tr key={t.id} className={`${rowClass(t)} hover:opacity-90`}>
                      <td className="px-4 py-3 font-mono text-xs text-gray-700">{t.site_id?.slice(0, 8)}…</td>
                      <td className="px-4 py-3 font-medium text-gray-700">{t.fault_type}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{t.reported_date}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{t.sla_due_date}</td>
                      <td className="px-4 py-3 text-center font-bold text-gray-700">
                        {t.days_elapsed ?? 0}
                      </td>
                      <td className="px-4 py-3">
                        <FaultStatusBadge status={t.task_status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Districts page
// ---------------------------------------------------------------------------
function Districts() {
  const { data, loading, error } = useFetch(`${API}/districts`);

  const byZone = useMemo(() => {
    if (!data) return {};
    return data.reduce((acc, d) => {
      const z = d.division || 'Unassigned';
      if (!acc[z]) acc[z] = [];
      acc[z].push(d);
      return acc;
    }, {});
  }, [data]);

  const zoneColors = {
    'Mysuru': 'bg-green-50 border-green-200',
    'Kalaburagi': 'bg-orange-50 border-orange-200',
    'North': 'bg-blue-50 border-blue-200',
    'South': 'bg-purple-50 border-purple-200',
    'Central': 'bg-yellow-50 border-yellow-200',
    'Coastal': 'bg-cyan-50 border-cyan-200',
    'Unassigned': 'bg-gray-50 border-gray-200',
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Districts</h1>
      {loading && <Spinner />}
      {error && <ErrorMsg msg={error} />}
      {!loading && !error && (
        data?.length === 0
          ? <EmptyState label="districts" />
          : <div className="space-y-6">
              {Object.entries(byZone).sort().map(([zone, dists]) => (
                <div key={zone} className={`rounded-xl border p-5 ${zoneColors[zone] || 'bg-gray-50 border-gray-200'}`}>
                  <h2 className="text-lg font-semibold text-gray-700 mb-3">
                    {zone} Zone
                    <span className="ml-2 text-sm font-normal text-gray-500">({dists.length} districts)</span>
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                    {dists.map(d => (
                      <div key={d.id} className="bg-white rounded-lg px-3 py-2 shadow-sm border">
                        <p className="font-medium text-gray-800 text-sm">{d.name}</p>
                        <p className="text-xs text-gray-500 font-mono">{d.code}</p>
                        {d.is_active && (
                          <span className="text-xs text-green-600 font-medium">● Active</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Materials Master page
// ---------------------------------------------------------------------------
function MaterialFormModal({ mode, material, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    name: material?.name || '',
    unit: material?.unit || '',
    category: material?.category || '',
    part_prefix: material?.part_prefix || '',
    is_active: material?.is_active ?? true,
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      const url = mode === 'add' ? `${API}/materials` : `${API}/materials/${material.id}`;
      const method = mode === 'add' ? 'POST' : 'PATCH';
      const body = {
        name: form.name,
        unit: form.unit || null,
        category: form.category || null,
        part_prefix: form.part_prefix || null,
        ...(mode === 'edit' && { is_active: form.is_active }),
      };
      const r = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      onSaved();
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-8">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-semibold text-gray-800">
            {mode === 'add' ? 'Add Material' : `Edit — ${material.name}`}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        {err && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelCls}>Name</label>
            <input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} required placeholder="e.g. GI Pipe" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Unit <span className="text-gray-400 font-normal">(optional)</span></label>
              <input className={inputCls} value={form.unit} onChange={e => set('unit', e.target.value)} placeholder="e.g. pcs, m, kg" />
            </div>
            <div>
              <label className={labelCls}>Category <span className="text-gray-400 font-normal">(optional)</span></label>
              <input className={inputCls} value={form.category} onChange={e => set('category', e.target.value)} placeholder="e.g. civil, unit, spare" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Part Prefix <span className="text-gray-400 font-normal">(optional)</span></label>
            <input className={inputCls} value={form.part_prefix} onChange={e => set('part_prefix', e.target.value)} placeholder="e.g. 5001-F-" />
          </div>
          {mode === 'edit' && (
            <div className="flex items-center gap-2">
              <input type="checkbox" id="mat_is_active" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} className="rounded" />
              <label htmlFor="mat_is_active" className="text-sm text-gray-700">Active</label>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60">
              {saving ? 'Saving…' : (mode === 'add' ? 'Add Material' : 'Save Changes')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Materials({ role }) {
  const canAddMaterial  = role === 'ADMIN' || role === 'BOM_CONTROLLER';
  const canEditMaterial = role === 'ADMIN' || role === 'BOM_CONTROLLER';

  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);
  const [editMaterial, setEditMaterial] = useState(null);

  const load = () => {
    setLoading(true);
    apiFetch(`${API}/materials?active=false`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => { setMaterials(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async m => {
    await apiFetch(`${API}/materials/${m.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: !m.is_active }),
    });
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Materials</h1>
        {canAddMaterial && (
          <button
            onClick={() => setModal('add')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
          >
            + Add Material
          </button>
        )}
      </div>

      {loading && <Spinner />}
      {error && <ErrorMsg msg={error} />}
      {!loading && !error && materials.length === 0 && <EmptyState label="materials" />}
      {!loading && !error && materials.length > 0 && (
        <div className="bg-white rounded-xl shadow overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['Name', 'Unit', 'Category', 'Part Prefix', 'Status', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {materials.map(m => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">{m.name}</td>
                  <td className="px-4 py-3 text-gray-600">{m.unit || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{m.category || '—'}</td>
                  <td className="px-4 py-3 text-gray-600 font-mono">{m.part_prefix || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${m.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {m.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {canEditMaterial && (
                      <>
                        <button
                          className="text-xs text-blue-600 hover:underline mr-3"
                          onClick={() => { setEditMaterial(m); setModal('edit'); }}
                        >Edit</button>
                        <button
                          className={`text-xs hover:underline ${m.is_active ? 'text-red-500' : 'text-green-600'}`}
                          onClick={() => toggleActive(m)}
                        >{m.is_active ? 'Deactivate' : 'Reactivate'}</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(modal === 'add' || modal === 'edit') && (
        <MaterialFormModal
          mode={modal}
          material={modal === 'edit' ? editMaterial : null}
          onClose={() => { setModal(null); setEditMaterial(null); }}
          onSaved={() => { setModal(null); setEditMaterial(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Personnel page
// ---------------------------------------------------------------------------
const WA_ICON = (
  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 inline-block text-green-500 ml-1" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.116 1.527 5.845L.057 23.177a.75.75 0 0 0 .92.92l5.332-1.47A11.946 11.946 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.885 0-3.65-.518-5.16-1.418l-.37-.218-3.833 1.057 1.057-3.833-.218-.37A9.951 9.951 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
  </svg>
);

function PersonnelFormModal({ mode, person, onClose, onSaved }) {
  const { data: districts } = useFetch(`${API}/districts`);

  const [form, setForm] = useState(() => ({
    name: person?.name || '',
    full_name: person?.full_name || '',
    role: person?.role || 'FieldStaff',
    phone: person?.phone || '',
    mobile2: person?.mobile2 || '',
    email: person?.email || '',
    district_ids: (person?.districts || []).map(d => d.id),
    division: person?.division || '',
    note: person?.note || '',
    telegram_chat_id: person?.telegram_chat_id || '',
    is_active: person?.is_active ?? true,
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const divisionOptions = useMemo(() =>
    districts ? [...new Set(districts.map(d => d.division).filter(Boolean))].sort() : [],
    [districts]);

  // Districts grouped by division for the checkbox panel
  const districtsByDivision = useMemo(() => {
    if (!districts) return {};
    const map = {};
    districts.forEach(d => {
      const div = d.division || 'Other';
      if (!map[div]) map[div] = [];
      map[div].push(d);
    });
    return map;
  }, [districts]);

  const toggleDistrict = id => {
    setForm(f => {
      const ids = f.district_ids.includes(id)
        ? f.district_ids.filter(x => x !== id)
        : [...f.district_ids, id];
      return { ...f, district_ids: ids };
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      const url = mode === 'add' ? `${API}/personnel` : `${API}/personnel/${person.id}`;
      const method = mode === 'add' ? 'POST' : 'PUT';
      const body = {
        name: form.name,
        full_name: form.full_name || null,
        role: form.role,
        phone: form.phone,
        mobile2: form.mobile2 || null,
        email: form.email || null,
        district_ids: form.district_ids,
        division: form.division || null,
        note: form.note || null,
        telegram_chat_id: form.telegram_chat_id || null,
        is_active: form.is_active,
        ...(mode === 'add' && { preferred_language: 'kn', max_sites: 100 }),
      };
      const r = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      onSaved();
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-8">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-semibold text-gray-800">
            {mode === 'add' ? 'Add New Staff' : `Edit — ${person.full_name || person.name}`}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        {err && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Username</label>
              <input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} required placeholder="e.g. alam" />
            </div>
            <div>
              <label className={labelCls}>Full Name</label>
              <input className={inputCls} value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Display name" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Role</label>
            <select className={inputCls} value={form.role} onChange={e => set('role', e.target.value)}>
              <option value="FieldStaff">FieldStaff — site surveys &amp; installation</option>
              <option value="BOMController">BOMController — materials dispatch</option>
              <option value="Coordinator">Coordinator</option>
              <option value="Manager">Manager</option>
              <option value="ProjectHead">ProjectHead</option>
              <option value="PMO">PMO</option>
              <option value="ITAdmin">ITAdmin</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Mobile 1 (WhatsApp)</label>
              <input className={inputCls} value={form.phone} onChange={e => set('phone', e.target.value)} required placeholder="10-digit mobile" />
            </div>
            <div>
              <label className={labelCls}>Mobile 2 <span className="text-gray-400 font-normal">(optional)</span></label>
              <input className={inputCls} value={form.mobile2} onChange={e => set('mobile2', e.target.value)} placeholder="Alternate number" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Division <span className="text-gray-400 font-normal">(for grouping staff)</span></label>
            <select className={inputCls} value={form.division} onChange={e => set('division', e.target.value)}>
              <option value="">Select division…</option>
              {divisionOptions.map(d => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>
              Districts
              {form.district_ids.length > 0 && (
                <span className="ml-2 text-blue-600 font-normal">{form.district_ids.length} selected</span>
              )}
            </label>
            <div className="border border-gray-200 rounded-lg p-3 max-h-52 overflow-y-auto bg-gray-50 space-y-3">
              {!districts && <p className="text-xs text-gray-400">Loading…</p>}
              {districts && Object.entries(districtsByDivision).sort().map(([div, dists]) => (
                <div key={div}>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{div}</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    {dists.map(d => (
                      <label key={d.id} className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.district_ids.includes(d.id)}
                          onChange={() => toggleDistrict(d.id)}
                          className="rounded accent-blue-600"
                        />
                        {d.name}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className={labelCls}>Note <span className="text-gray-400 font-normal">(optional)</span></label>
            <input className={inputCls} value={form.note} onChange={e => set('note', e.target.value)} placeholder="e.g. covers Ballari and Vijayanagara" />
          </div>
          <div>
            <label className={labelCls}>Email <span className="text-gray-400 font-normal">(optional — for email alerts)</span></label>
            <input type="email" className={inputCls} value={form.email} onChange={e => set('email', e.target.value)} placeholder="staff@example.com" />
          </div>
          <div>
            <label className={labelCls}>Telegram Chat ID <span className="text-gray-400 font-normal">(optional)</span></label>
            <input className={inputCls} value={form.telegram_chat_id} onChange={e => set('telegram_chat_id', e.target.value)} placeholder="e.g. 78412559" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="is_active" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} className="rounded" />
            <label htmlFor="is_active" className="text-sm text-gray-700">Active</label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60">
              {saving ? 'Saving…' : (mode === 'add' ? 'Add Staff' : 'Save Changes')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RegenerateLinkConfirmModal({ onConfirm, onCancel }) {
  const [busy, setBusy] = useState(false);
  const handleConfirm = async () => {
    setBusy(true);
    await onConfirm();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">Regenerate Link?</h2>
        <p className="text-sm text-gray-600 mb-5">
          This will invalidate the current link — anyone using it will lose access. Continue?
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleConfirm}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-60"
          >
            {busy ? 'Regenerating…' : 'Regenerate'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldFormLinkCell({ person, canEdit, onChanged }) {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [err, setErr] = useState(null);

  const fetchLink = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await apiFetch(`${API}/personnel/${person.id}/field-form-link`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setLink(j.link);
      setOpen(true);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  const generate = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await apiFetch(`${API}/personnel/${person.id}/field-form-token`, { method: 'POST' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setLink(j.link);
      setOpen(true);
      onChanged();
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  const doRegenerate = async () => {
    setConfirmRegen(false);
    await generate();
  };

  const copy = () => {
    navigator.clipboard?.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative inline-block text-left">
      {!person.has_field_form_token && (
        canEdit ? (
          <button
            onClick={generate}
            disabled={loading}
            className="text-xs text-blue-600 hover:underline disabled:opacity-60"
          >
            {loading ? 'Generating…' : 'Generate link'}
          </button>
        ) : (
          <span className="text-xs text-gray-400">Not generated</span>
        )
      )}
      {person.has_field_form_token && (
        <button
          onClick={fetchLink}
          disabled={loading}
          className="text-xs text-green-700 hover:underline disabled:opacity-60"
        >
          {loading ? 'Loading…' : '✅ Link generated'}
        </button>
      )}
      {err && <p className="text-xs text-red-500 mt-1">{err}</p>}
      {open && link && (
        <div className="absolute right-0 z-20 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-left">
          <div className="flex justify-between items-start mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Installation Form Link</p>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 text-sm leading-none">&times;</button>
          </div>
          <p className="text-xs font-mono text-gray-700 break-all bg-gray-50 border border-gray-200 rounded p-2 mb-2">{link}</p>
          <div className="flex justify-between items-center gap-2">
            <button onClick={copy} className="text-xs text-blue-600 hover:underline">
              {copied ? 'Copied!' : 'Copy link'}
            </button>
            {canEdit && (
              <button onClick={() => setConfirmRegen(true)} className="text-xs text-amber-600 hover:underline">
                Regenerate
              </button>
            )}
          </div>
        </div>
      )}
      {confirmRegen && (
        <RegenerateLinkConfirmModal
          onConfirm={doRegenerate}
          onCancel={() => setConfirmRegen(false)}
        />
      )}
    </div>
  );
}

function Personnel({ role }) {
  const canAddPersonnel    = role !== 'VIEWER';
  const canEditPersonnel   = role !== 'VIEWER';
  const canDeletePersonnel = role === 'ADMIN';

  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);
  const [editPerson, setEditPerson] = useState(null);
  const [deletePerson, setDeletePerson] = useState(null);

  const load = () => {
    setLoading(true);
    apiFetch(`${API}/personnel`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => { setStaff(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const byDivision = useMemo(() => {
    const map = {};
    staff.forEach(p => {
      const div = p.districts?.[0]?.division || p.division || 'Unassigned';
      if (!map[div]) map[div] = [];
      map[div].push(p);
    });
    return map;
  }, [staff]);

  const divisionOrder = ['Mysuru', 'Kalaburagi', 'Unassigned'];
  const orderedDivisions = [
    ...divisionOrder.filter(d => byDivision[d]),
    ...Object.keys(byDivision).filter(d => !divisionOrder.includes(d)).sort(),
  ];

  const divColors = {
    Mysuru: 'bg-green-50 border-green-300 text-green-800',
    Kalaburagi: 'bg-orange-50 border-orange-300 text-orange-800',
    Unassigned: 'bg-gray-50 border-gray-300 text-gray-600',
  };

  const handleDelete = async () => {
    await apiFetch(`${API}/personnel/${deletePerson.id}`, { method: 'DELETE' });
    setDeletePerson(null);
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Personnel</h1>
        {canAddPersonnel && (
          <button
            onClick={() => setModal('add')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
          >
            + Add New Staff
          </button>
        )}
      </div>

      {loading && <Spinner />}
      {error && <ErrorMsg msg={error} />}
      {!loading && !error && staff.length === 0 && <EmptyState label="personnel" />}
      {!loading && !error && staff.length > 0 && (
        <div className="space-y-6">
          {orderedDivisions.map(div => (
            <div key={div}>
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-semibold mb-3 ${divColors[div] || 'bg-gray-50 border-gray-300 text-gray-600'}`}>
                {div} Division
                <span className="font-normal opacity-70">· {byDivision[div].length} staff</span>
              </div>
              <div className="bg-white rounded-xl shadow overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      {['Full Name', 'District', 'Mobile 1', 'Mobile 2', 'Note', 'Install Form Link', ''].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {byDivision[div].map(p => (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-800">{p.full_name || p.name}</p>
                          <p className="text-xs text-gray-400 font-mono">{p.name}</p>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{p.districts?.length ? p.districts.map(d => d.name).join(', ') : '—'}</td>
                        <td className="px-4 py-3 text-gray-700 font-mono whitespace-nowrap">
                          {p.phone}
                          {WA_ICON}
                        </td>
                        <td className="px-4 py-3 text-gray-500 font-mono whitespace-nowrap">{p.mobile2 || '—'}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 italic max-w-[200px]">{p.note || ''}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {p.role === 'FieldStaff'
                            ? <FieldFormLinkCell person={p} canEdit={canEditPersonnel} onChanged={load} />
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {canEditPersonnel && (
                            <button
                              className="text-xs text-blue-600 hover:underline mr-3"
                              onClick={() => { setEditPerson(p); setModal('edit'); }}
                            >Edit</button>
                          )}
                          {canDeletePersonnel && (
                            <button
                              className="text-xs text-red-500 hover:underline"
                              onClick={() => setDeletePerson(p)}
                            >Delete</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {(modal === 'add' || modal === 'edit') && (
        <PersonnelFormModal
          mode={modal}
          person={modal === 'edit' ? editPerson : null}
          onClose={() => { setModal(null); setEditPerson(null); }}
          onSaved={() => { setModal(null); setEditPerson(null); load(); }}
        />
      )}

      {deletePerson && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setDeletePerson(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Delete Staff?</h2>
            <p className="text-sm text-gray-600 mb-5">
              Permanently delete <span className="font-semibold">{deletePerson.full_name || deletePerson.name}</span>? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeletePerson(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// User Management page (Admin only)
// ---------------------------------------------------------------------------
function UserFormModal({ mode, user, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    username: user?.username || '',
    full_name: user?.full_name || '',
    email: user?.email || '',
    password: '',
    role: user?.role || 'VIEWER',
    is_active: user?.is_active ?? true,
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      const url = mode === 'add' ? `${API}/auth/users` : `${API}/auth/users/${user.id}`;
      const method = mode === 'add' ? 'POST' : 'PUT';
      const body = mode === 'add'
        ? {
            username: form.username,
            full_name: form.full_name,
            email: form.email || null,
            password: form.password,
            role: form.role,
            is_active: form.is_active,
          }
        : {
            full_name: form.full_name,
            email: form.email || null,
            role: form.role,
            is_active: form.is_active,
            ...(form.password ? { password: form.password } : {}),
          };
      const r = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      onSaved();
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-8">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-semibold text-gray-800">
            {mode === 'add' ? 'Add New User' : `Edit — ${user.username}`}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        {err && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'add' && (
            <div>
              <label className={labelCls}>Username</label>
              <input className={inputCls} value={form.username} onChange={e => set('username', e.target.value)} required placeholder="login username" />
            </div>
          )}
          <div>
            <label className={labelCls}>Full Name</label>
            <input className={inputCls} value={form.full_name} onChange={e => set('full_name', e.target.value)} required />
          </div>
          <div>
            <label className={labelCls}>Email <span className="text-gray-400 font-normal">(optional)</span></label>
            <input type="email" className={inputCls} value={form.email} onChange={e => set('email', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>
              {mode === 'add' ? 'Password' : 'New Password'}
              {mode === 'edit' && <span className="text-gray-400 font-normal ml-1">(leave blank to keep current)</span>}
            </label>
            <input
              type="password"
              className={inputCls}
              value={form.password}
              onChange={e => set('password', e.target.value)}
              required={mode === 'add'}
              placeholder={mode === 'edit' ? '••••••••' : 'Enter password'}
            />
          </div>
          <div>
            <label className={labelCls}>Role</label>
            <select className={inputCls} value={form.role} onChange={e => set('role', e.target.value)}>
              <option value="VIEWER">VIEWER — read only</option>
              <option value="SUPERVISOR">SUPERVISOR — assign, update, override</option>
              <option value="BOM_CONTROLLER">BOM_CONTROLLER — assign, update, override</option>
              <option value="ADMIN">ADMIN — full access</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="user_is_active" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} className="rounded" />
            <label htmlFor="user_is_active" className="text-sm text-gray-700">Active</label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60">
              {saving ? 'Saving…' : (mode === 'add' ? 'Add User' : 'Save Changes')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);

  const load = () => {
    setLoading(true);
    apiFetch(`${API}/auth/users`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => { setUsers(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async () => {
    try {
      await apiFetch(`${API}/auth/users/${deleteUser.id}`, { method: 'DELETE' });
    } catch (e) {
      alert(e.message);
    }
    setDeleteUser(null);
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">User Management</h1>
        <button
          onClick={() => setModal('add')}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
        >
          + Add User
        </button>
      </div>

      {loading && <Spinner />}
      {error && <ErrorMsg msg={error} />}
      {!loading && !error && (
        users.length === 0
          ? <EmptyState label="users" />
          : <div className="bg-white rounded-xl shadow overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {['Username', 'Full Name', 'Email', 'Role', 'Status', 'Created', ''].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.map(u => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-sm font-medium text-gray-800">{u.username}</td>
                      <td className="px-4 py-3 text-gray-700">{u.full_name}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{u.email || '—'}</td>
                      <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${u.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          className="text-xs text-blue-600 hover:underline mr-3"
                          onClick={() => { setEditUser(u); setModal('edit'); }}
                        >Edit</button>
                        <button
                          className="text-xs text-red-500 hover:underline"
                          onClick={() => setDeleteUser(u)}
                        >Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
      )}

      {(modal === 'add' || modal === 'edit') && (
        <UserFormModal
          mode={modal}
          user={modal === 'edit' ? editUser : null}
          onClose={() => { setModal(null); setEditUser(null); }}
          onSaved={() => { setModal(null); setEditUser(null); load(); }}
        />
      )}

      {deleteUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setDeleteUser(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Delete User?</h2>
            <p className="text-sm text-gray-600 mb-5">
              Permanently delete <span className="font-semibold font-mono">{deleteUser.username}</span>? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteUser(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Settings page (Admin only)
// ---------------------------------------------------------------------------
function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveErr, setSaveErr] = useState(null);

  useEffect(() => {
    apiFetch(`${API}/settings`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => {
        setSettings(data);
        const map = {};
        data.forEach(s => { map[s.key] = s.value || ''; });
        setForm(map);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  const handleSave = async e => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    setSaveErr(null);
    try {
      const keys = ['smtp_host', 'smtp_port', 'smtp_user', 'smtp_password', 'smtp_from_name', 'smtp_enabled'];
      for (const key of keys) {
        if (form[key] !== undefined) {
          const r = await apiFetch(`${API}/settings/${key}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ value: form[key] }),
          });
          if (!r.ok) {
            const j = await r.json().catch(() => ({}));
            throw new Error(j.detail || `HTTP ${r.status}`);
          }
        }
      }
      setSaved(true);
    } catch (e) {
      setSaveErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

  return (
    <div className="max-w-xl">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Settings</h1>
      {loading && <Spinner />}
      {error && <ErrorMsg msg={error} />}
      {!loading && !error && (
        <form onSubmit={handleSave} className="bg-white rounded-xl shadow p-6 space-y-5">
          <h2 className="text-base font-semibold text-gray-700 border-b pb-2">SMTP Email Configuration</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>SMTP Host</label>
              <input className={inputCls} value={form.smtp_host || ''} onChange={e => setForm(f => ({ ...f, smtp_host: e.target.value }))} placeholder="smtp.gmail.com" />
            </div>
            <div>
              <label className={labelCls}>SMTP Port</label>
              <input className={inputCls} value={form.smtp_port || ''} onChange={e => setForm(f => ({ ...f, smtp_port: e.target.value }))} placeholder="587" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Username</label>
            <input className={inputCls} value={form.smtp_user || ''} onChange={e => setForm(f => ({ ...f, smtp_user: e.target.value }))} placeholder="alerts@example.com" />
          </div>
          <div>
            <label className={labelCls}>Password</label>
            <input type="password" className={inputCls} value={form.smtp_password || ''} onChange={e => setForm(f => ({ ...f, smtp_password: e.target.value }))} placeholder="••••••••" />
          </div>
          <div>
            <label className={labelCls}>From Name</label>
            <input className={inputCls} value={form.smtp_from_name || ''} onChange={e => setForm(f => ({ ...f, smtp_from_name: e.target.value }))} placeholder="Spatika Rain Gauge" />
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, smtp_enabled: f.smtp_enabled === 'true' ? 'false' : 'true' }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.smtp_enabled === 'true' ? 'bg-blue-600' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${form.smtp_enabled === 'true' ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
            <span className="text-sm text-gray-700">Enable Email Alerts</span>
          </div>
          {saved && <p className="text-sm text-green-600">Settings saved.</p>}
          {saveErr && <p className="text-sm text-red-600">{saveErr}</p>}
          <div className="flex justify-end pt-2">
            <button type="submit" disabled={saving} className="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60">
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sidebar + Layout
// ---------------------------------------------------------------------------
function Sidebar({ open, onClose, userRole }) {
  const navItems = [
    { to: '/', label: 'Dashboard', icon: '🏠', end: true },
    { to: '/sites', label: 'Sites', icon: '🗺️' },
    { to: '/fault-tasks', label: 'Fault Tasks', icon: '⚠️' },
    { to: '/districts', label: 'Districts', icon: '📍' },
    { to: '/personnel', label: 'Personnel', icon: '👷' },
    { to: '/materials', label: 'Materials', icon: '📦' },
    { to: '/packing', label: 'Pending Packing', icon: '📋' },
    { to: '/installation-reports', label: 'Installation Reports', icon: '🛠️' },
    ...(userRole === 'ADMIN' || userRole === 'SUPERVISOR' ? [{ to: '/bulk-tools', label: 'Bulk Tools', icon: '🧰' }] : []),
    ...(userRole === 'ADMIN' ? [{ to: '/users', label: 'Users', icon: '👥' }] : []),
    ...(userRole === 'ADMIN' ? [{ to: '/settings', label: 'Settings', icon: '⚙️' }] : []),
  ];

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-20 lg:hidden"
          onClick={onClose}
        />
      )}
      <aside className={[
        'fixed top-0 left-0 h-full w-64 bg-gray-900 text-white z-30 flex flex-col',
        'transform transition-transform duration-200',
        'lg:translate-x-0 lg:static lg:h-screen lg:flex-shrink-0',
        open ? 'translate-x-0' : '-translate-x-full',
      ].join(' ')}>
        <div className="px-6 py-5 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🌧️</span>
            <div>
              <p className="font-bold text-white leading-tight">Spatika</p>
              <p className="text-xs text-gray-400">Rain Gauge Dashboard</p>
            </div>
          </div>
        </div>
        <nav className="px-3 py-4 space-y-1 flex-1 overflow-y-auto min-h-0">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onClose}
              className={({ isActive }) =>
                [
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white',
                ].join(' ')
              }
            >
              <span className="text-lg">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-6 py-4 border-t border-gray-700 flex-shrink-0">
          <p className="text-xs text-gray-500">KSNDMC</p>
          <p className="text-xs text-gray-600">TRG · 2026</p>
        </div>
      </aside>
    </>
  );
}

function Layout({ children, currentUser, onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} userRole={currentUser?.role} />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-white border-b px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-md text-gray-500 hover:bg-gray-100 lg:hidden"
              aria-label="Open menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <span className="font-semibold text-gray-700 lg:hidden">🌧️ Spatika Rain Gauge</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-gray-800 leading-tight">{currentUser?.full_name}</p>
              <div className="mt-0.5"><RoleBadge role={currentUser?.role} /></div>
            </div>
            <button
              onClick={onLogout}
              className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Logout
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Login page
// ---------------------------------------------------------------------------
function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || 'Invalid credentials');
      }
      const data = await r.json();
      _token = data.access_token;
      onLogin({ username: data.username, full_name: data.full_name, role: data.role });
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-8">
        <div className="text-center mb-8">
          <span className="text-5xl">🌧️</span>
          <h1 className="text-2xl font-bold text-gray-800 mt-3">Spatika Rain Gauge</h1>
          <p className="text-sm text-gray-500 mt-1">KSNDMC · Field Operations</p>
        </div>
        {error && (
          <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <input
              type="text"
              autoComplete="username"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={username}
              onChange={e => setUsername(e.target.value)}
              required
              placeholder="Enter your username"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              autoComplete="current-password"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors mt-2"
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dispatch Confirm — public, no login required
// ---------------------------------------------------------------------------
function DispatchConfirm() {
  const { token } = useParams();
  const [info, setInfo] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [confirmed, setConfirmed] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);

  React.useEffect(() => {
    fetch(`/api/dispatch/${token}`)
      .then(r => r.json().then(j => ({ ok: r.ok, body: j })))
      .then(({ ok, body }) => {
        if (!ok) throw new Error(body.detail || 'Invalid or expired link');
        setInfo(body);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const r = await fetch(`/api/dispatch/${token}/confirm`, { method: 'POST' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Confirmation failed');
      setConfirmed(true);
    } catch (e) {
      setError(e.message);
      setConfirming(false);
    }
  };

  const location = info ? [info.district_name, info.taluk, info.gp].filter(Boolean).join(' / ') : '';
  const actionLabel = info?.action === 'CIVIL_MTLS_DISPATCHED' ? 'Civil Materials' : 'Installation Units';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-400 text-sm">Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow p-8 max-w-sm w-full text-center">
          <div className="text-4xl mb-4">❌</div>
          <p className="text-red-600 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  if (info?.used || confirmed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow p-8 max-w-sm w-full text-center">
          <div className="text-5xl mb-4">✅</div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">Dispatch Confirmed</h1>
          <p className="text-sm text-gray-500">
            {actionLabel} for <strong>{location || info?.govt_site_id}</strong> has been marked as dispatched.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-8">
        <div className="text-5xl text-center mb-4">🚚</div>
        <h1 className="text-xl font-bold text-gray-800 text-center mb-1">Confirm Dispatch</h1>
        <p className="text-sm text-gray-500 text-center mb-6">{actionLabel}</p>
        <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-200">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Site</p>
          <p className="text-base font-bold text-gray-800">{location || info?.govt_site_id || '—'}</p>
          {info?.govt_site_id && location && (
            <p className="text-xs text-gray-400 font-mono mt-1">{info.govt_site_id}</p>
          )}
        </div>
        <button
          className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-60"
          disabled={confirming}
          onClick={handleConfirm}
        >
          {confirming ? 'Confirming…' : '✓ Confirm Dispatch'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dispatch Batch Confirm — public, no login required
// ---------------------------------------------------------------------------
function DispatchBatchConfirm() {
  const { token } = useParams();
  const [info, setInfo] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [confirmed, setConfirmed] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);

  React.useEffect(() => {
    fetch(`/api/dispatch-batch/${token}`)
      .then(r => r.json().then(j => ({ ok: r.ok, body: j })))
      .then(({ ok, body }) => {
        if (!ok) throw new Error(body.detail || 'Invalid or expired link');
        setInfo(body);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const r = await fetch(`/api/dispatch-batch/${token}/confirm`, { method: 'POST' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Confirmation failed');
      setConfirmed(true);
    } catch (e) {
      setError(e.message);
      setConfirming(false);
    }
  };

  const materialsLabel = info?.materials_kind === 'civil' ? 'Civil Materials' : info?.materials_kind === 'units' ? 'Installation Units' : '';
  const siteCount = info?.sites?.length || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-400 text-sm">Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow p-8 max-w-sm w-full text-center">
          <div className="text-4xl mb-4">❌</div>
          <p className="text-red-600 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  if (info?.used || confirmed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow p-8 max-w-sm w-full text-center">
          <div className="text-5xl mb-4">✅</div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">Dispatch Confirmed</h1>
          <p className="text-sm text-gray-500">
            {materialsLabel} for <strong>{siteCount} site{siteCount !== 1 ? 's' : ''}</strong> has been marked as dispatched.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-8">
        <div className="text-5xl text-center mb-4">🚚</div>
        <h1 className="text-xl font-bold text-gray-800 text-center mb-1">Confirm Dispatch</h1>
        <p className="text-sm text-gray-500 text-center mb-6">{materialsLabel}</p>
        <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-200 max-h-64 overflow-y-auto">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            {siteCount} Site{siteCount !== 1 ? 's' : ''}
          </p>
          <div className="space-y-2">
            {(info?.sites || []).map(s => {
              const loc = [s.district_name, s.taluk, s.gp].filter(Boolean).join(' / ');
              return (
                <div key={s.site_id} className="text-sm border-t border-gray-200 pt-2 first:border-0 first:pt-0">
                  <p className="font-medium text-gray-800">{loc || s.govt_site_id || '—'}</p>
                  {s.govt_site_id && loc && (
                    <p className="text-xs text-gray-400 font-mono">{s.govt_site_id}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <button
          className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-60"
          disabled={confirming}
          onClick={handleConfirm}
        >
          {confirming ? 'Confirming…' : '✓ Confirm Dispatch'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Material Receipt Confirm — public, no login required
// ---------------------------------------------------------------------------
function MaterialReceiptConfirm() {
  const { token } = useParams();
  const [info, setInfo] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [confirming, setConfirming] = React.useState(false);

  React.useEffect(() => {
    fetch(`/api/material-receipt/confirm/${token}`)
      .then(r => r.json().then(j => ({ ok: r.ok, body: j })))
      .then(({ ok, body }) => {
        if (!ok) throw new Error(body.detail || 'Invalid link');
        setInfo(body);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const r = await fetch(`/api/material-receipt/confirm/${token}`, { method: 'POST' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Confirmation failed');
      setInfo(j);
    } catch (e) {
      setError(e.message);
    } finally {
      setConfirming(false);
    }
  };

  const materialsLabel = info?.materials_kind === 'civil' ? 'Civil Materials' : info?.materials_kind === 'units' ? 'Installation Units' : '';
  const location = info ? [info.district_name, info.taluk, info.gp].filter(Boolean).join(' / ') : '';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-400 text-sm">Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow p-8 max-w-sm w-full text-center">
          <div className="text-4xl mb-4">❌</div>
          <p className="text-red-600 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  if (info?.confirmed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow p-8 max-w-sm w-full text-center">
          <div className="text-5xl mb-4">✅</div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">Already Confirmed</h1>
          <p className="text-sm text-gray-500">
            Receipt for <strong>{location || info?.govt_site_id}</strong> was confirmed on{' '}
            {new Date(info.confirmed_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-8">
        <div className="text-5xl text-center mb-4">📦</div>
        <h1 className="text-xl font-bold text-gray-800 text-center mb-1">Confirm Receipt</h1>
        <p className="text-sm text-gray-500 text-center mb-6">{materialsLabel}</p>
        <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-200">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Site</p>
          <p className="text-base font-bold text-gray-800">{location || info?.govt_site_id || '—'}</p>
          {info?.govt_site_id && location && (
            <p className="text-xs text-gray-400 font-mono mt-1">{info.govt_site_id}</p>
          )}
          {info?.is_fallback && (
            <p className="text-xs text-amber-600 mt-2">
              No installer assigned yet — confirming at Field Store.
            </p>
          )}
        </div>
        <button
          className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-60"
          disabled={confirming}
          onClick={handleConfirm}
        >
          {confirming ? 'Confirming…' : '✓ Confirm Receipt'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Survey Form — public, no login required
// ---------------------------------------------------------------------------
function PhotoUploadStep({ surveyId, staffName, onDone }) {
  const PHOTOS = [
    { type: 'NORTH_SOUTH', label: 'North-South Photo' },
    { type: 'EAST_WEST',   label: 'East-West Photo' },
    { type: 'ADDITIONAL',  label: 'Additional Photo', optional: true },
  ];
  const [status, setStatus] = React.useState({ NORTH_SOUTH: null, EAST_WEST: null, ADDITIONAL: null });

  const upload = async (photoType, file) => {
    setStatus(s => ({ ...s, [photoType]: 'uploading' }));
    try {
      const fd = new FormData();
      fd.append('photo_type', photoType);
      fd.append('file', file, file.name || 'photo.jpg');
      const r = await fetch(`/api/survey/photo/${surveyId}`, { method: 'POST', body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Upload failed');
      setStatus(s => ({ ...s, [photoType]: 'done' }));
    } catch (err) {
      setStatus(s => ({ ...s, [photoType]: err.message }));
    }
  };

  const anyDone = Object.values(status).some(s => s === 'done');

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center p-4 pt-10 pb-16">
      <div className="text-5xl mb-3">✅</div>
      <h2 className="text-xl font-bold text-green-700 mb-1">Survey Submitted!</h2>
      <p className="text-gray-500 text-sm mb-8 text-center">
        {staffName ? `Thank you, ${staffName}. ` : ''}Now upload site photos.
      </p>

      <div className="w-full max-w-sm space-y-4">
        {PHOTOS.map(({ type, label, optional }) => {
          const s = status[type];
          const isDone = s === 'done';
          const isUploading = s === 'uploading';
          const isError = s && s !== 'done' && s !== 'uploading';
          return (
            <label key={type} className={`block cursor-pointer ${isUploading ? 'pointer-events-none' : ''}`}>
              <div className={`flex items-center gap-4 p-5 rounded-2xl border-2 transition-colors ${
                isDone    ? 'border-green-400 bg-green-50' :
                isError   ? 'border-red-300 bg-red-50' :
                            'border-gray-200 bg-white'
              }`}>
                <span className="text-3xl">{isDone ? '✅' : '📷'}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-800">
                    {label}
                    {optional && <span className="text-gray-400 font-normal text-xs ml-2">(optional)</span>}
                  </div>
                  <div className="text-xs mt-0.5">
                    {!s         && <span className="text-gray-400">Tap to select photo</span>}
                    {isUploading && <span className="text-blue-500">Uploading…</span>}
                    {isDone      && <span className="text-green-600">Uploaded ✓</span>}
                    {isError     && <span className="text-red-600 break-all">{s} — tap to retry</span>}
                  </div>
                </div>
              </div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => { const f = e.target.files[0]; if (f) upload(type, f); }}
              />
            </label>
          );
        })}
      </div>

      <button
        onClick={onDone}
        className="mt-8 w-full max-w-sm py-5 bg-green-600 text-white font-extrabold text-xl rounded-2xl shadow-lg active:bg-green-700"
      >
        {anyDone ? 'Done' : 'Skip Photos'}
      </button>
    </div>
  );
}

function PhotoUpload({ label, required, file, onChange, error }) {
  const [preview, setPreview] = React.useState(null);
  const [previewFailed, setPreviewFailed] = React.useState(false);

  React.useEffect(() => {
    if (!file) {
      setPreview(null);
      setPreviewFailed(false);
      return;
    }
    setPreviewFailed(false);
    try {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.onerror = () => setPreviewFailed(true);
      reader.readAsDataURL(file);
    } catch (err) {
      setPreviewFailed(true);
    }
  }, [file]);

  const handleChange = e => {
    try {
      onChange(e.target.files[0] || null);
    } catch (err) {
      setPreviewFailed(true);
    }
  };

  return (
    <div className={`border-2 border-dashed rounded-2xl overflow-hidden ${file ? 'border-green-400' : 'border-gray-300'}`}>
      <label className="block cursor-pointer">
        {file ? (
          <div className="relative">
            {previewFailed || !preview ? (
              <div className="w-full h-32 bg-gray-100 flex flex-col items-center justify-center gap-1">
                <span className="text-3xl">🖼️</span>
                <p className="text-xs text-gray-500">Preview not available</p>
                <p className="text-xs text-gray-400 truncate max-w-xs">{file.name}</p>
              </div>
            ) : (
              <img
                src={preview}
                alt="preview"
                className="w-full max-h-52 object-cover"
                onError={() => setPreviewFailed(true)}
              />
            )}
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-40 text-white text-xs text-center py-1">
              {label} ✓ — tap to change
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-6 px-4">
            <span className="text-5xl">📷</span>
            <span className="text-gray-700 font-semibold text-center">{label}</span>
            {required && <span className="text-xs text-red-500 font-medium">Required</span>}
            <span className="text-xs text-gray-400">Tap to open camera</span>
          </div>
        )}
        <input
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          capture="environment"
          className="hidden"
          onChange={handleChange}
        />
      </label>
      {error && (
        <div className="bg-amber-50 border-t border-amber-200 px-3 py-2 text-xs text-amber-700">
          ⚠️ {error}
        </div>
      )}
    </div>
  );
}

function AdditionalThumb({ file }) {
  const [src, setSrc] = React.useState(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    try {
      const reader = new FileReader();
      reader.onload = (e) => setSrc(e.target.result);
      reader.onerror = () => setFailed(true);
      reader.readAsDataURL(file);
    } catch (err) {
      setFailed(true);
    }
  }, [file]);

  if (failed || !src) {
    return (
      <div className="w-full aspect-square bg-gray-100 flex items-center justify-center">
        <span className="text-2xl">🖼️</span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt=""
      className="w-full aspect-square object-cover"
      onError={() => setFailed(true)}
    />
  );
}

function SurveyForm() {
  const { token } = useParams();
  const [site, setSite] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState('');

  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [gpsLoading, setGpsLoading] = useState(false);
  const [airtelSignal, setAirtelSignal] = useState('');
  const [bsnlSignal, setBsnlSignal] = useState('');
  const [jioSignal, setJioSignal] = useState('');
  const [vaName, setVaName] = useState('');
  const [vaPhone, setVaPhone] = useState('');
  const [helperName, setHelperName] = useState('');
  const [helperPhone, setHelperPhone] = useState('');
  const [installType, setInstallType] = useState('');
  const [rooftopPosition, setRooftopPosition] = useState('');
  const [treesNearby, setTreesNearby] = useState(null);
  const [bushesOnSite, setBushesOnSite] = useState(null);
  const [materialsAvailable, setMaterialsAvailable] = useState(null);
  const [materialsRemarks, setMaterialsRemarks] = useState('');
  const [contractorAvailable, setContractorAvailable] = useState(null);
  const [contractorName, setContractorName] = useState('');
  const [contractorPhone, setContractorPhone] = useState('');
  const [remarks, setRemarks] = useState('');
  const [surveyId, setSurveyId] = useState(null);
  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [gpsLocked, setGpsLocked] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const [gpsSyncStatus, setGpsSyncStatus] = useState(null); // null | 'pending' | 'synced'

  useEffect(() => {
    fetch(`/api/survey/${token}`)
      .then(r => r.json())
      .then(d => {
        if (d.detail) throw new Error(d.detail);
        setSite(d);
        try {
          const raw = localStorage.getItem(`survey_draft_${token}`);
          if (raw) {
            const draft = JSON.parse(raw);
            if (Date.now() - new Date(draft.savedAt).getTime() <= 7 * 24 * 60 * 60 * 1000) {
              if (draft.lat) setLat(draft.lat);
              if (draft.lon) setLon(draft.lon);
              if (draft.airtelSignal) setAirtelSignal(draft.airtelSignal);
              if (draft.bsnlSignal) setBsnlSignal(draft.bsnlSignal);
              if (draft.jioSignal) setJioSignal(draft.jioSignal);
              if (draft.vaName) setVaName(draft.vaName);
              if (draft.vaPhone) setVaPhone(draft.vaPhone);
              if (draft.helperName) setHelperName(draft.helperName);
              if (draft.helperPhone) setHelperPhone(draft.helperPhone);
              if (draft.installType) setInstallType(draft.installType);
              if (draft.rooftopPosition) setRooftopPosition(draft.rooftopPosition);
              if (draft.treesNearby != null) setTreesNearby(draft.treesNearby);
              if (draft.bushesOnSite != null) setBushesOnSite(draft.bushesOnSite);
              if (draft.materialsAvailable != null) setMaterialsAvailable(draft.materialsAvailable);
              if (draft.materialsRemarks) setMaterialsRemarks(draft.materialsRemarks);
              if (draft.contractorAvailable != null) setContractorAvailable(draft.contractorAvailable);
              if (draft.contractorName) setContractorName(draft.contractorName);
              if (draft.contractorPhone) setContractorPhone(draft.contractorPhone);
              if (draft.remarks) setRemarks(draft.remarks);
              if (draft.gpsLocked) {
                setGpsLocked(true);
                if (localStorage.getItem(`survey_gps_pending_${token}`) === 'true') {
                  setGpsSyncStatus('pending');
                }
              }
              setDraftRestored(true);
            } else {
              localStorage.removeItem(`survey_draft_${token}`);
            }
          }
        } catch {}
        setPageLoading(false);
      })
      .catch(e => { setPageError(e.message); setPageLoading(false); });
  }, [token]);

  useEffect(() => {
    if (!site) return;
    const draft = {
      lat, lon, airtelSignal, bsnlSignal, jioSignal,
      vaName, vaPhone, helperName, helperPhone,
      installType, rooftopPosition,
      treesNearby, bushesOnSite,
      materialsAvailable, materialsRemarks,
      contractorAvailable, contractorName, contractorPhone,
      remarks, gpsLocked,
      savedAt: new Date().toISOString(),
    };
    try { localStorage.setItem(`survey_draft_${token}`, JSON.stringify(draft)); } catch {}
  }, [site, token, lat, lon, airtelSignal, bsnlSignal, jioSignal, vaName, vaPhone, helperName, helperPhone, installType, rooftopPosition, treesNearby, bushesOnSite, materialsAvailable, materialsRemarks, contractorAvailable, contractorName, contractorPhone, remarks, gpsLocked]);

  useEffect(() => {
    if (gpsSyncStatus !== 'pending' || !lat || !lon) return;
    const retry = async () => {
      try {
        const r = await fetch(`/api/survey/${token}/gps`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ latitude: parseFloat(lat), longitude: parseFloat(lon), captured_at: new Date().toISOString() }),
        });
        if (r.ok) {
          localStorage.removeItem(`survey_gps_pending_${token}`);
          setGpsSyncStatus('synced');
        }
      } catch {}
    };
    window.addEventListener('online', retry);
    if (navigator.onLine) retry();
    return () => window.removeEventListener('online', retry);
  }, [gpsSyncStatus, lat, lon, token]);

  const getLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        const newLat = pos.coords.latitude.toFixed(7);
        const newLon = pos.coords.longitude.toFixed(7);
        setLat(newLat);
        setLon(newLon);
        setGpsLocked(true);
        setGpsLoading(false);
        try {
          const raw = localStorage.getItem(`survey_draft_${token}`);
          const existing = raw ? JSON.parse(raw) : {};
          localStorage.setItem(`survey_draft_${token}`, JSON.stringify({
            ...existing, lat: newLat, lon: newLon, gpsLocked: true,
            savedAt: new Date().toISOString(),
          }));
        } catch {}
        fetch(`/api/survey/${token}/gps`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            latitude: parseFloat(newLat),
            longitude: parseFloat(newLon),
            captured_at: new Date().toISOString(),
          }),
        }).then(r => {
          if (r.ok) {
            localStorage.removeItem(`survey_gps_pending_${token}`);
            setGpsSyncStatus('synced');
          } else {
            localStorage.setItem(`survey_gps_pending_${token}`, 'true');
            setGpsSyncStatus('pending');
          }
        }).catch(() => {
          localStorage.setItem(`survey_gps_pending_${token}`, 'true');
          setGpsSyncStatus('pending');
        });
      },
      () => {
        setGpsLoading(false);
        alert('Could not get location. Please allow location access and try again.');
      },
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };

  const canSubmit = lat && lon && !submitting;

  const handleSubmit = async e => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      console.log('Building FormData...');
      const fd = new FormData();
      console.log('lat:', lat, 'lon:', lon);
      fd.append('latitude', lat ? parseFloat(lat) : '');
      fd.append('longitude', lon ? parseFloat(lon) : '');
      fd.append('airtel_signal', airtelSignal);
      fd.append('bsnl_signal', bsnlSignal);
      fd.append('jio_signal', jioSignal);
      fd.append('va_name', vaName);
      fd.append('va_phone', vaPhone);
      fd.append('helper_name', helperName);
      fd.append('helper_phone', helperPhone);
      fd.append('installation_type', installType);
      fd.append('rooftop_position', rooftopPosition);
      fd.append('trees_nearby', treesNearby ? 'true' : 'false');
      fd.append('bushes_on_site', bushesOnSite ? 'true' : 'false');
      fd.append('materials_available', materialsAvailable ? 'true' : 'false');
      fd.append('materials_remarks', materialsRemarks);
      fd.append('contractor_available', contractorAvailable ? 'true' : 'false');
      fd.append('contractor_name', contractorName);
      fd.append('contractor_phone', contractorPhone);
      fd.append('remarks', remarks);
      console.log('Sending fetch...');
      const r = await fetch(`/api/survey/${token}`, { method: 'POST', body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Submission failed');
      setSubmittedName(data.staff_name || '');
      setSurveyId(data.survey_id);
      setSubmitted(true);
      try { localStorage.removeItem(`survey_draft_${token}`); } catch {}
    } catch (err) {
      console.error('Submit error:', err);
      console.error('Stack:', err.stack);
      setSubmitError('Error: ' + err.message + ' | ' + err.stack);
    } finally {
      setSubmitting(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (pageError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6 bg-red-50">
        <div className="text-6xl mb-4">❌</div>
        <h2 className="text-xl font-bold text-red-700 mb-2">Invalid Survey Link</h2>
        <p className="text-red-600 text-center">{pageError}</p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6 bg-green-50">
        <div className="text-7xl mb-4">✅</div>
        <h2 className="text-2xl font-bold text-green-700 mb-3">Survey Complete!</h2>
        <p className="text-green-700 text-center text-lg">
          Thank you{submittedName ? `, ${submittedName}` : ''}.
        </p>
      </div>
    );
  }

  if (submitted) {
    return (
      <PhotoUploadStep
        surveyId={surveyId}
        staffName={submittedName}
        onDone={() => setDone(true)}
      />
    );
  }

  const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const locationLabel = [site.district, site.taluk, site.gp].filter(Boolean).join(' / ');

  const SignalRow = ({ label, value, onChange }) => (
    <div className="mb-4">
      <div className="text-sm font-semibold text-gray-600 mb-2">{label}</div>
      <div className="flex gap-2">
        {[['GOOD', '✅ Good', 'bg-green-500'], ['WEAK', '⚠️ Weak', 'bg-yellow-500'], ['NONE', '❌ None', 'bg-red-500']].map(([v, lbl, cls]) => (
          <button key={v} type="button" onClick={() => onChange(v)}
            className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-colors ${value === v ? `${cls} text-white` : 'bg-gray-100 text-gray-600'}`}>
            {lbl}
          </button>
        ))}
      </div>
    </div>
  );

  const YesNoToggle = ({ label, value, onChange }) => (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <span className="text-gray-700 font-medium">{label}</span>
      <div className="flex gap-2">
        {[true, false].map(b => (
          <button key={String(b)} type="button" onClick={() => onChange(b)}
            className={`px-5 py-2 rounded-xl font-semibold text-sm transition-colors ${
              value === b
                ? b ? 'bg-green-500 text-white' : 'bg-red-400 text-white'
                : 'bg-gray-100 text-gray-500'
            }`}>
            {b ? 'Yes' : 'No'}
          </button>
        ))}
      </div>
    </div>
  );


  return (
    <div className="min-h-screen bg-gray-50">
      <a
        href={window.location.href}
        target="_blank"
        rel="noopener noreferrer"
        style={{display:'block', textAlign:'center',
          padding:'10px', background:'#007AFF',
          color:'white', borderRadius:'8px',
          margin:'10px', textDecoration:'none'}}
      >
        🌐 For best experience, open this link in Chrome browser
      </a>
      <div style={{display:'block', textAlign:'center',
        padding:'8px 12px', background:'#FFF3CD',
        color:'#856404', borderRadius:'8px',
        margin:'0 10px', fontSize:'13px'}}>
        💡 Tip: Keep this page open while at the site. Do not close the browser tab.
      </div>
      {draftRestored && (
        <div className="bg-green-50 border border-green-300 text-green-800 px-4 py-3 mx-3 mt-2 rounded-xl flex items-start justify-between gap-3">
          <span className="text-sm">✅ Draft restored from your last session. Continue filling or tap Clear to start fresh.</span>
          <button
            type="button"
            onClick={() => {
              try { localStorage.removeItem(`survey_draft_${token}`); } catch {}
              setDraftRestored(false);
              setLat(''); setLon('');
              setAirtelSignal(''); setBsnlSignal(''); setJioSignal('');
              setVaName(''); setVaPhone('');
              setHelperName(''); setHelperPhone('');
              setInstallType(''); setRooftopPosition('');
              setTreesNearby(null); setBushesOnSite(null);
              setMaterialsAvailable(null); setMaterialsRemarks('');
              setContractorAvailable(null); setContractorName(''); setContractorPhone('');
              setRemarks('');
              setGpsLocked(false);
            }}
            className="shrink-0 text-xs font-bold text-green-700 border border-green-400 px-3 py-1 rounded-lg whitespace-nowrap"
          >
            Clear Draft
          </button>
        </div>
      )}
      {/* Sticky location bar */}
      <div style={{position:'sticky', top:0, zIndex:50}}
        className="bg-white border-l-4 border-blue-600 shadow-md px-4 py-2 flex items-center gap-2">
        <span className="text-blue-600 text-base">📍</span>
        <span className="font-bold text-gray-800 text-sm truncate">{locationLabel}</span>
      </div>

      {/* Header */}
      <div className="bg-blue-700 text-white px-4 pt-10 pb-8">
        <div className="text-center max-w-lg mx-auto">
          <p className="text-xl font-extrabold tracking-wide">KSNDMC</p>
          <p className="text-blue-200 text-xs mt-0.5">Karnataka State Natural Disaster Monitoring Centre</p>
          <h1 className="text-2xl font-bold mt-4">TRG Survey Form</h1>
          <p className="text-blue-100 font-semibold mt-2 text-base">{locationLabel}</p>
          <p className="text-blue-300 text-sm mt-1">{today}</p>
        </div>
      </div>

      <form noValidate onSubmit={handleSubmit} className="p-4 space-y-5 max-w-lg mx-auto pb-12">

        {/* Section 1 — Location */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <h2 className="text-base font-bold text-gray-800 mb-4">📍 Location</h2>
          <div className="grid grid-cols-2 gap-3 mb-4">
            {[['Latitude', lat, setLat], ['Longitude', lon, setLon]].map(([lbl, val, setter]) => (
              <div key={lbl}>
                <label className="block text-xs font-semibold text-gray-500 mb-1">{lbl} {gpsLocked && '🔒'}</label>
                <input type="text" inputMode="decimal" value={val}
                  onChange={e => { if (!gpsLocked) setter(e.target.value); }}
                  readOnly={gpsLocked}
                  className={`w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none${gpsLocked ? ' bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                  placeholder="0.0000000" />
              </div>
            ))}
          </div>
          {!gpsLocked && (
            <button type="button" onClick={getLocation} disabled={gpsLoading}
              className="w-full py-4 bg-blue-600 text-white font-bold rounded-xl text-base disabled:opacity-60 flex items-center justify-center gap-2 active:bg-blue-700">
              {gpsLoading
                ? <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Getting Location…</>
                : <>📍 Get My Location</>}
            </button>
          )}
          {lat && lon && (
            <p className="text-center text-xs text-green-600 font-medium mt-2">✓ {lat}, {lon}{gpsLocked ? ' 🔒' : ''}</p>
          )}
          {gpsSyncStatus === 'pending' && (
            <p className="text-center text-xs text-amber-600 font-medium mt-1">📍 GPS saved locally — will sync when online</p>
          )}
          {gpsSyncStatus === 'synced' && (
            <p className="text-center text-xs text-green-600 font-medium mt-1">📍 GPS synced ✅</p>
          )}
        </div>

        {/* Section 2 — Network */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <h2 className="text-base font-bold text-gray-800 mb-4">📶 Network Availability</h2>
          <SignalRow label="Airtel" value={airtelSignal} onChange={setAirtelSignal} />
          <SignalRow label="BSNL" value={bsnlSignal} onChange={setBsnlSignal} />
          <SignalRow label="Jio" value={jioSignal} onChange={setJioSignal} />
        </div>

        {/* Section 3 — Contacts */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <h2 className="text-base font-bold text-gray-800 mb-4">👥 Local Contacts</h2>
          <div className="space-y-4">
            {[
              ['Village Accountant Name', vaName, setVaName, 'Full name', false, undefined],
              ['Village Accountant Phone', vaPhone, setVaPhone, '+91 XXXXX XXXXX', false, 'tel'],
              ['Helper Name', helperName, setHelperName, 'Full name', true, undefined],
              ['Helper Phone', helperPhone, setHelperPhone, '+91 XXXXX XXXXX', true, 'tel'],
            ].map(([lbl, val, setter, placeholder, optional, inputMode]) => (
              <div key={lbl}>
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  {lbl}{optional && <span className="text-gray-400 font-normal ml-1">(optional)</span>}
                </label>
                <input type="text" inputMode={inputMode} value={val} onChange={e => setter(e.target.value)}
                  className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder={placeholder} />
              </div>
            ))}
          </div>
        </div>

        {/* Section 4 — Site Assessment */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <h2 className="text-base font-bold text-gray-800 mb-4">🏗️ Site Assessment</h2>

          <div className="mb-5">
            <p className="text-xs font-semibold text-gray-500 mb-2">Installation Type</p>
            <div className="grid grid-cols-2 gap-3">
              {[['GROUND', '🏗️ Ground'], ['ROOFTOP', '🏠 Rooftop']].map(([v, lbl]) => (
                <button key={v} type="button" onClick={() => setInstallType(v)}
                  className={`py-5 rounded-2xl font-bold text-base transition-colors ${installType === v ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          {installType === 'ROOFTOP' && (
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Rooftop Position</label>
              <textarea value={rooftopPosition} onChange={e => setRooftopPosition(e.target.value)} rows={2}
                className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
                placeholder="Describe position on rooftop…" />
            </div>
          )}

          <YesNoToggle label="Trees Nearby" value={treesNearby} onChange={setTreesNearby} />
          <YesNoToggle label="Bushes on Site" value={bushesOnSite} onChange={setBushesOnSite} />
          <YesNoToggle label="Materials Available" value={materialsAvailable} onChange={setMaterialsAvailable} />

          {materialsAvailable && (
            <div className="mt-3 mb-2">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Materials Remarks</label>
              <textarea value={materialsRemarks} onChange={e => setMaterialsRemarks(e.target.value)} rows={2}
                className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
                placeholder="Describe available materials…" />
            </div>
          )}

          {site?.install_status !== 'CIVIL_PENDING' && (
            <>
              <YesNoToggle label="Civil Contractor Available" value={contractorAvailable} onChange={setContractorAvailable} />

              {contractorAvailable && (
                <div className="mt-3 space-y-3">
                  <input value={contractorName} onChange={e => setContractorName(e.target.value)}
                    className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="Contractor Name" />
                  <input type="text" inputMode="tel" value={contractorPhone} onChange={e => setContractorPhone(e.target.value)}
                    className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="Contractor Phone" />
                </div>
              )}
            </>
          )}
        </div>

        {/* Section 5 — Remarks */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <h2 className="text-base font-bold text-gray-800 mb-3">📝 Remarks</h2>
          <textarea value={remarks} onChange={e => setRemarks(e.target.value)} rows={4}
            className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
            placeholder="Any other observations or notes…" />
        </div>

        {!canSubmit && !submitting && (
          <div className="text-center text-sm text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded-xl">
            {!lat || !lon ? '📍 Please get your GPS location.' : ''}
          </div>
        )}

        {submitError && (
          <div className="bg-red-50 border border-red-300 text-red-700 text-xs p-3 rounded-xl whitespace-pre-wrap break-all">
            {submitError}
          </div>
        )}

        <button type="submit" disabled={!canSubmit}
          className="w-full py-5 bg-green-600 text-white font-extrabold text-xl rounded-2xl disabled:opacity-50 flex items-center justify-center gap-3 shadow-lg active:bg-green-700">
          {submitting
            ? <><div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin" /> Uploading…</>
            : 'Submit Survey'}
        </button>

      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Installation & Commissioning Form — public, no login required
// (Personal, persistent link — not single-use, remains usable across jobs)
// ---------------------------------------------------------------------------
function InstallFileInput({ label, file, onChange, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type="file"
        accept="image/*"
        onChange={e => onChange(e.target.files[0] || null)}
        className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 file:text-sm file:font-medium"
      />
      {file && <p className="text-xs text-green-600 mt-1">✓ {file.name}</p>}
    </div>
  );
}

function InstallCheckbox({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-3 py-2 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="w-5 h-5 rounded" />
      <span className="text-sm text-gray-700 font-medium">{label}</span>
    </label>
  );
}

function InstallToggle({ label, value, onChange, trueLabel = 'OK', falseLabel = 'Not OK' }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <span className="text-gray-700 font-medium text-sm">{label}</span>
      <div className="flex gap-2">
        <button type="button" onClick={() => onChange('ok')}
          className={`px-4 py-2 rounded-lg font-semibold text-xs transition-colors ${value === 'ok' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
          {trueLabel}
        </button>
        <button type="button" onClick={() => onChange('not_ok')}
          className={`px-4 py-2 rounded-lg font-semibold text-xs transition-colors ${value === 'not_ok' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
          {falseLabel}
        </button>
      </div>
    </div>
  );
}

function InstallNumberInput({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
      <input
        type="number"
        inputMode="decimal"
        step="any"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
        placeholder="0.0"
      />
    </div>
  );
}

function InstallTextInput({ label, value, onChange, placeholder = '' }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
      <input
        type="tel"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
        placeholder={placeholder}
      />
    </div>
  );
}

// Shared by InstallationForm (field submission) and the Installation Reports
// admin edit mode. Self-contained: owns its own focus ref, so multiple
// instances (e.g. desktop + mobile layouts mounted simultaneously) never
// fight over a single ref the way a lifted ref map would.
function PartNumberInput({ value, prefix, onChange, placeholder }) {
  const inputRef = useRef(null);

  const insertPrefix = () => {
    onChange(prefix);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(prefix.length, prefix.length);
      }
    });
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Tab' || e.shiftKey || !prefix) return;
    if (value !== '') return;
    e.preventDefault();
    insertPrefix();
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
        placeholder={placeholder || prefix || 'Enter serial number'}
      />
      {value === '' && prefix && (
        <button
          type="button"
          onClick={insertPrefix}
          className="mt-1.5 inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100"
        >
          Use {prefix}
        </button>
      )}
    </div>
  );
}

function InstallationForm() {
  const { token } = useParams();
  const [formData, setFormData] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [activeTab, setActiveTab] = useState('parts');
  const [openAccordion, setOpenAccordion] = useState(() => new Set());

  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState(null);

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const [partSerials, setPartSerials] = useState({});

  const [simInserted, setSimInserted] = useState(false);
  const [mobileNumber, setMobileNumber] = useState('');
  const [sitePhoto, setSitePhoto] = useState(null);
  const [noLoadLfp, setNoLoadLfp] = useState('');
  const [noLoadLiion, setNoLoadLiion] = useState('');
  const [noLoadSolar1, setNoLoadSolar1] = useState('');
  const [noLoadSolar2, setNoLoadSolar2] = useState('');

  const [wiringRf, setWiringRf] = useState(false);
  const [wiringGsm, setWiringGsm] = useState(false);
  const [wiringSolar, setWiringSolar] = useState(false);
  const [wiringDatalogger, setWiringDatalogger] = useState(false);
  const [underLoadLfp, setUnderLoadLfp] = useState('');
  const [underLoadLiion, setUnderLoadLiion] = useState('');
  const [underLoadSolar1, setUnderLoadSolar1] = useState('');
  const [underLoadSolar2, setUnderLoadSolar2] = useState('');
  const [powerUpOk, setPowerUpOk] = useState(null);
  const [stationIdAssigned, setStationIdAssigned] = useState(false);
  const [stationId, setStationId] = useState('');
  const [networkOk, setNetworkOk] = useState(null);
  const [signalStrength, setSignalStrength] = useState('');
  const [manualTipOk, setManualTipOk] = useState(null);
  const [switchedOffRemoved, setSwitchedOffRemoved] = useState(false);

  const [directConnection, setDirectConnection] = useState(false);
  const [dataLoggingConfirmed, setDataLoggingConfirmed] = useState(false);
  const [finalCalibrationPass, setFinalCalibrationPass] = useState(null);

  const [photoNorth, setPhotoNorth] = useState(null);
  const [photoEast, setPhotoEast] = useState(null);
  const [photoSouth, setPhotoSouth] = useState(null);
  const [photoWest, setPhotoWest] = useState(null);

  const [healthSmsSent, setHealthSmsSent] = useState(false);
  const [remarks, setRemarks] = useState('');

  useEffect(() => {
    fetch(`/api/installation-form/${token}`)
      .then(r => r.json().then(j => ({ ok: r.ok, body: j })))
      .then(({ ok, body }) => {
        if (!ok) throw new Error(body.detail || 'Invalid link');
        setFormData(body);
      })
      .catch(e => setPageError(e.message))
      .finally(() => setPageLoading(false));
  }, [token]);

  const selectedJob = (formData?.jobs || []).find(j => j.site_id === selectedSiteId) || null;
  const activeMaterials = (formData?.materials || []).filter(
    m => m.name.trim().toLowerCase() !== 'sim card'
  );

  useEffect(() => {
    if (!selectedJob) { setPartSerials({}); return; }
    const prefillMap = {};
    for (const p of selectedJob.parts_prefill) prefillMap[p.material_id] = p.part_no || '';
    setPartSerials(prefillMap);
  }, [selectedSiteId]); // eslint-disable-line

  const captureGps = () => {
    if (!navigator.geolocation) {
      setGpsError('Geolocation is not supported by your browser.');
      return;
    }
    setGpsLoading(true);
    setGpsError(null);
    navigator.geolocation.getCurrentPosition(
      pos => {
        setLat(pos.coords.latitude.toFixed(7));
        setLon(pos.coords.longitude.toFixed(7));
        setGpsLoading(false);
      },
      () => {
        setGpsLoading(false);
        setGpsError('Could not get location. Please allow location access and try again.');
      },
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };

  const directionPhotoCount = [photoNorth, photoEast, photoSouth, photoWest].filter(Boolean).length;

  const validate = () => {
    const errs = [];
    if (!selectedSiteId) errs.push('Please select a site.');
    if (!lat || !lon) errs.push('GPS location must be captured.');
    if (!sitePhoto) errs.push('Site photo (Stage 1) is required.');
    if (directionPhotoCount < 2) errs.push('At least 2 of the 4 direction photos are required.');
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (errs.length) { setValidationErrors(errs); return; }
    setValidationErrors([]);
    setSubmitting(true);
    setSubmitError(null);
    try {
      const partsPayload = activeMaterials
        .map(m => {
          const partNo = (partSerials[m.id] || '').trim();
          if (!partNo) return null;
          return { material_id: m.id, part_no: partNo };
        })
        .filter(Boolean);

      const details = {
        stage1: {
          sim_card_inserted: simInserted,
          mobile_number: mobileNumber.trim() || null,
          no_load_voltage_lfp: noLoadLfp === '' ? null : parseFloat(noLoadLfp),
          no_load_voltage_liion: noLoadLiion === '' ? null : parseFloat(noLoadLiion),
          no_load_voltage_solar1: noLoadSolar1 === '' ? null : parseFloat(noLoadSolar1),
          no_load_voltage_solar2: noLoadSolar2 === '' ? null : parseFloat(noLoadSolar2),
        },
        stage2: {
          wiring_rf_sensor_tipping_bucket: wiringRf,
          wiring_gsm_antenna: wiringGsm,
          wiring_solar_panel: wiringSolar,
          wiring_datalogger_through_tester: wiringDatalogger,
          under_load_voltage_lfp: underLoadLfp === '' ? null : parseFloat(underLoadLfp),
          under_load_voltage_liion: underLoadLiion === '' ? null : parseFloat(underLoadLiion),
          under_load_voltage_solar1: underLoadSolar1 === '' ? null : parseFloat(underLoadSolar1),
          under_load_voltage_solar2: underLoadSolar2 === '' ? null : parseFloat(underLoadSolar2),
          power_up_ok: powerUpOk === null ? null : powerUpOk === 'ok',
          station_id_assigned: stationIdAssigned,
          station_id: stationIdAssigned ? stationId : null,
          network_ok: networkOk === null ? null : networkOk === 'ok',
          signal_strength: signalStrength === '' ? null : parseFloat(signalStrength),
          manual_tip_check_ok: manualTipOk === null ? null : manualTipOk === 'ok',
          switched_off_tester_removed: switchedOffRemoved,
        },
        stage3: {
          direct_connection_done: directConnection,
          data_logging_confirmed: dataLoggingConfirmed,
          final_calibration_pass: finalCalibrationPass === null ? null : finalCalibrationPass === 'ok',
        },
        health_sms_sent: healthSmsSent,
        remarks,
      };

      const fd = new FormData();
      fd.append('site_id', selectedSiteId);
      fd.append('gps_lat', lat);
      fd.append('gps_lon', lon);
      fd.append('parts_json', JSON.stringify(partsPayload));
      fd.append('details_json', JSON.stringify(details));
      fd.append('site_photo', sitePhoto, sitePhoto.name || 'site_photo.jpg');
      if (photoNorth) fd.append('photo_north', photoNorth, photoNorth.name || 'north.jpg');
      if (photoEast) fd.append('photo_east', photoEast, photoEast.name || 'east.jpg');
      if (photoSouth) fd.append('photo_south', photoSouth, photoSouth.name || 'south.jpg');
      if (photoWest) fd.append('photo_west', photoWest, photoWest.name || 'west.jpg');

      const r = await fetch(`/api/installation-form/${token}/submit`, { method: 'POST', body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Submission failed');
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderParts = () => (
    <div className="space-y-4">
      {!selectedSiteId && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3">
          Select a site above to enter part serial numbers.
        </p>
      )}
      {selectedSiteId && activeMaterials.length === 0 && (
        <p className="text-sm text-gray-400">No materials configured.</p>
      )}
      {selectedSiteId && activeMaterials.map(m => (
        <div key={m.id}>
          <label className="block text-xs font-semibold text-gray-500 mb-1">{m.name}</label>
          <PartNumberInput
            value={partSerials[m.id] || ''}
            prefix={m.part_prefix || ''}
            onChange={v => setPartSerials(s => ({ ...s, [m.id]: v }))}
          />
        </div>
      ))}
    </div>
  );

  const renderStage1 = () => (
    <div className="space-y-5">
      <InstallCheckbox label="SIM card inserted" checked={simInserted} onChange={setSimInserted} />
      <InstallTextInput label="Mobile Number" value={mobileNumber} onChange={setMobileNumber} placeholder="Optional" />
      <InstallFileInput label="Site Photo" file={sitePhoto} onChange={setSitePhoto} required />
      <div className="grid grid-cols-2 gap-3">
        <InstallNumberInput label="No-load Voltage — LFP" value={noLoadLfp} onChange={setNoLoadLfp} />
        <InstallNumberInput label="No-load Voltage — Li-ion" value={noLoadLiion} onChange={setNoLoadLiion} />
        <InstallNumberInput label="No-load Voltage — Solar 1" value={noLoadSolar1} onChange={setNoLoadSolar1} />
        <InstallNumberInput label="No-load Voltage — Solar 2" value={noLoadSolar2} onChange={setNoLoadSolar2} />
      </div>
    </div>
  );

  const renderStage2 = () => (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-semibold text-gray-500 mb-2">Wiring Tasks</p>
        <InstallCheckbox label="RF sensor / tipping bucket wired to datalogger" checked={wiringRf} onChange={setWiringRf} />
        <InstallCheckbox label="GSM antenna connected" checked={wiringGsm} onChange={setWiringGsm} />
        <InstallCheckbox label="Solar panel connected to power pack" checked={wiringSolar} onChange={setWiringSolar} />
        <InstallCheckbox label="Datalogger connected through tester" checked={wiringDatalogger} onChange={setWiringDatalogger} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <InstallNumberInput label="Under-load Voltage — LFP" value={underLoadLfp} onChange={setUnderLoadLfp} />
        <InstallNumberInput label="Under-load Voltage — Li-ion" value={underLoadLiion} onChange={setUnderLoadLiion} />
        <InstallNumberInput label="Under-load Voltage — Solar 1" value={underLoadSolar1} onChange={setUnderLoadSolar1} />
        <InstallNumberInput label="Under-load Voltage — Solar 2" value={underLoadSolar2} onChange={setUnderLoadSolar2} />
      </div>
      <InstallToggle label="Power-up" value={powerUpOk} onChange={setPowerUpOk} />
      <InstallCheckbox label="Station ID assigned" checked={stationIdAssigned} onChange={setStationIdAssigned} />
      {stationIdAssigned && (
        <input
          type="text"
          value={stationId}
          onChange={e => setStationId(e.target.value)}
          className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          placeholder="Station ID"
        />
      )}
      <InstallToggle label="Network" value={networkOk} onChange={setNetworkOk} />
      <InstallNumberInput label="Signal Strength" value={signalStrength} onChange={setSignalStrength} />
      <InstallToggle label="Manual Tip Check" value={manualTipOk} onChange={setManualTipOk} />
      <InstallCheckbox label="Switched off / tester removed" checked={switchedOffRemoved} onChange={setSwitchedOffRemoved} />
    </div>
  );

  const renderStage3 = () => (
    <div className="space-y-2">
      <InstallCheckbox label="Direct connection done" checked={directConnection} onChange={setDirectConnection} />
      <InstallCheckbox label="Data logging confirmed" checked={dataLoggingConfirmed} onChange={setDataLoggingConfirmed} />
      <InstallToggle label="Final Calibration" value={finalCalibrationPass} onChange={setFinalCalibrationPass} trueLabel="Pass" falseLabel="Fail" />
    </div>
  );

  const renderPhotos = () => (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">{directionPhotoCount} of 4 selected — at least 2 required</p>
      <InstallFileInput label="North" file={photoNorth} onChange={setPhotoNorth} />
      <InstallFileInput label="East" file={photoEast} onChange={setPhotoEast} />
      <InstallFileInput label="South" file={photoSouth} onChange={setPhotoSouth} />
      <InstallFileInput label="West" file={photoWest} onChange={setPhotoWest} />
    </div>
  );

  const renderFinalize = () => (
    <div className="space-y-4">
      <InstallCheckbox label="Health SMS sent" checked={healthSmsSent} onChange={setHealthSmsSent} />
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1">Remarks</label>
        <textarea
          value={remarks}
          onChange={e => setRemarks(e.target.value)}
          rows={4}
          className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
          placeholder="Any other observations…"
        />
      </div>
      {validationErrors.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 text-amber-800 text-sm p-3 rounded-xl space-y-1">
          {validationErrors.map((e, i) => <p key={i}>⚠️ {e}</p>)}
        </div>
      )}
      {submitError && (
        <div className="bg-red-50 border border-red-300 text-red-700 text-sm p-3 rounded-xl">{submitError}</div>
      )}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full py-5 bg-green-600 text-white font-extrabold text-xl rounded-2xl disabled:opacity-50 flex items-center justify-center gap-3 shadow-lg active:bg-green-700"
      >
        {submitting
          ? <><div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin" /> Submitting…</>
          : 'Submit'}
      </button>
    </div>
  );

  const SECTIONS = [
    { key: 'parts', label: 'Parts', render: renderParts },
    { key: 'stage1', label: '1 · Tester on Power Pack', render: renderStage1 },
    { key: 'stage2', label: '2 · Tester with Datalogger', render: renderStage2 },
    { key: 'stage3', label: '3 · Direct Connection', render: renderStage3 },
    { key: 'photos', label: 'Photos', render: renderPhotos },
    { key: 'finalize', label: 'Finalize', render: renderFinalize },
  ];

  if (pageLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (pageError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6 bg-red-50">
        <div className="text-6xl mb-4">❌</div>
        <h2 className="text-xl font-bold text-red-700 mb-2">Invalid Form Link</h2>
        <p className="text-red-600 text-center">{pageError}</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        <div className="text-7xl mb-4">✅</div>
        <h2 className="text-2xl font-bold text-green-700 mb-3">Installation Submitted!</h2>
        <p className="text-green-700 text-center text-base mb-6">
          Thank you{formData?.staff_name ? `, ${formData.staff_name}` : ''}.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl"
        >
          Submit Another Installation
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-blue-700 text-white px-4 pt-10 pb-6">
        <div className="text-center max-w-lg mx-auto">
          <p className="text-xl font-extrabold tracking-wide">KSNDMC</p>
          <h1 className="text-2xl font-bold mt-2">Installation &amp; Commissioning</h1>
          <p className="text-blue-100 font-semibold mt-2">{formData.staff_name}</p>
        </div>
      </div>

      <div className="p-4 max-w-lg mx-auto pb-12">
        <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
          <label className="block text-xs font-semibold text-gray-500 mb-1">Site</label>
          <select
            value={selectedSiteId}
            onChange={e => setSelectedSiteId(e.target.value)}
            className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none mb-4"
          >
            <option value="">Select a site…</option>
            {(formData.jobs || []).map(j => (
              <option key={j.site_id} value={j.site_id}>
                {j.site_code} — {[j.district_name, j.taluk, j.gp].filter(Boolean).join(' / ')}
              </option>
            ))}
          </select>
          {(formData.jobs || []).length === 0 && (
            <p className="text-xs text-amber-600 mb-4">No pending installation jobs are currently assigned to you.</p>
          )}

          <label className="block text-xs font-semibold text-gray-500 mb-1">GPS Location <span className="text-red-500">*</span></label>
          {lat && lon ? (
            <p className="text-sm text-green-600 font-medium mb-2">✓ {lat}, {lon}</p>
          ) : (
            <p className="text-sm text-gray-400 mb-2">Not captured yet</p>
          )}
          <button
            type="button"
            onClick={captureGps}
            disabled={gpsLoading}
            className="w-full py-3 bg-blue-600 text-white font-bold rounded-xl text-sm disabled:opacity-60"
          >
            {gpsLoading ? 'Getting location…' : (lat && lon ? '📍 Re-capture Location' : '📍 Capture GPS Location')}
          </button>
          {gpsError && <p className="text-xs text-red-600 mt-1">{gpsError}</p>}

          <p className="text-xs text-gray-400 mt-3">Visit time: {now.toLocaleString('en-IN')}</p>
        </div>

        {/* Desktop / wide viewport — tabs */}
        <div className="hidden sm:block">
          <div className="flex border-b border-gray-200 mb-4 overflow-x-auto">
            {SECTIONS.map(s => (
              <button
                key={s.key}
                type="button"
                onClick={() => setActiveTab(s.key)}
                className={`px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 ${activeTab === s.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-5">
            {SECTIONS.find(s => s.key === activeTab)?.render('desktop')}
          </div>
        </div>

        {/* Narrow viewport (<640px) — accordion */}
        <div className="sm:hidden space-y-3">
          {SECTIONS.map(s => {
            const isOpen = openAccordion.has(s.key);
            return (
              <div key={s.key} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenAccordion(prev => {
                    const next = new Set(prev);
                    if (next.has(s.key)) next.delete(s.key); else next.add(s.key);
                    return next;
                  })}
                  className="w-full flex items-center justify-between px-5 py-4 text-left"
                >
                  <span className="font-bold text-gray-800 text-sm">{s.label}</span>
                  <span className="text-gray-400">{isOpen ? '▲' : '▼'}</span>
                </button>
                {isOpen && <div className="px-5 pb-5">{s.render('mobile')}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// App root
// ---------------------------------------------------------------------------
export default function App() {
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    _onUnauth = () => setCurrentUser(null);
    return () => { _onUnauth = null; };
  }, []);

  const handleLogout = async () => {
    try {
      await apiFetch(`${API}/auth/logout`, { method: 'POST' });
    } catch {}
    _token = null;
    setCurrentUser(null);
  };

  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes — no login required */}
        <Route path="/survey/:token" element={<SurveyForm />} />
        <Route path="/dispatch/:token" element={<DispatchConfirm />} />
        <Route path="/dispatch-batch/:token" element={<DispatchBatchConfirm />} />
        <Route path="/material-receipt/confirm/:token" element={<MaterialReceiptConfirm />} />
        <Route path="/installation-form/:token" element={<InstallationForm />} />

        {/* All other routes require login */}
        <Route
          path="*"
          element={
            currentUser ? (
              <Layout currentUser={currentUser} onLogout={handleLogout}>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/sites" element={<Sites role={currentUser.role} />} />
                  <Route path="/fault-tasks" element={<FaultTasks />} />
                  <Route path="/districts" element={<Districts />} />
                  <Route path="/personnel" element={<Personnel role={currentUser.role} />} />
                  <Route path="/materials" element={<Materials role={currentUser.role} />} />
                  <Route path="/packing" element={<PendingPacking role={currentUser.role} />} />
                  <Route path="/installation-reports" element={<InstallationReports role={currentUser.role} />} />
                  {(currentUser.role === 'ADMIN' || currentUser.role === 'SUPERVISOR') && (
                    <Route path="/bulk-tools" element={<BulkTools />} />
                  )}
                  {currentUser.role === 'ADMIN' && (
                    <Route path="/users" element={<Users />} />
                  )}
                  {currentUser.role === 'ADMIN' && (
                    <Route path="/settings" element={<Settings />} />
                  )}
                </Routes>
              </Layout>
            ) : (
              <LoginPage onLogin={setCurrentUser} />
            )
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
